import { CurrencyPipe, DatePipe } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit, inject } from '@angular/core';
import { DocumentBrandingService } from 'app/core/services/document-branding/document-branding.service';
import { Router } from "@angular/router";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatOptionSelectionChange } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import { MatSelectChange } from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { ApplicationInfoDto, BomDto, BomItemDto, ComponentSourceType, CostCentreDto, CustomerContactListItemDto, CustomerInvoiceAddressDto, CustomerListItemDto, CustomerResponse, DealCategory, DealDto, DealQuoteCalibrationLineDto, DealQuoteHireLineDto, DealQuoteMarineLineDto, DealQuotePartLineDto, DealQuoteProductLineDto, DealRevisionSummaryDto, DealStageDto, DealType, DlmProductDto, EnumViewModel, FreightTerm, Incoterm, LeadTimeType, LifecycleStatus, LoadcellResponse, ManufactureProcessCategoryBreakdownDto, ManufactureProcessDto, MarinePartDto, OperationCodeDto, OrderDirection, PartFileDto, PartLocationDto, PartLocationType, PartResponse, PartResponseWithSubstitutes, PartUnitType, ProductDto, ProductMarkupConfigDto, PurchaseOrderDto, PurchaseOrderHireItemDto, PurchaseOrderPartItemDto, PurchaseOrderProductItemDto, QuoteFileDto, TeamRole, TransportMode, UserDto, VatOption } from "app/api/models";
import { MarineService, BomService, CostCentreService, CustomerService, DealService, DlmProductService, EmployeeService, ManufactureProcessService, OperationCodeService, PartService, PickSheetService, ProductMarkupConfigService, ProductService, PurchaseOrderService, RepairsAndCalibrationsService, SettingsService } from "app/api/services";
import { AppAuthService } from "app/api/services/cookie-jwt-auth/app-auth.service";
import { getUniqueOnSelf, sortOnProperty, sumList } from "app/app.component";
import { AddItemDialogComponent, EnumViewModelWithInfo } from "app/core/components/add-item-dialog/add-item-dialog.component";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import { GenericInfoDialogService } from "app/core/services/generic-info-dialog/infoDialog.service";
import { DealHelperService } from "app/core/services/deal/deal-helper.service";
import { TimeUtil } from "app/core/utils/time-utils";
import { UploadFilePopupComponent } from "app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component";
import { AssemblyProcessPopupComponent } from "app/views/admin/pages/product-register/boms/bom-builder/assembly-process-popup/assembly-process-popup.component";
import { ImportManufactureProcessDialogComponent } from "app/views/admin/pages/product-register/boms/bom-builder/import-manufacture-process-dialog/import-manufacture-process-dialog.component";
import { RecordPartQuoteDialogComponent } from "app/views/admin/pages/parts-register/record-part-quote-dialog/record-part-quote-dialog.component";
import { Paging } from "app/views/calibrations/shared/components/paging";
import { CellInformationDialogComponent } from "app/views/calibrations/views/repairs-and-calibrations/cell-information-dialog/cell-information-dialog.component";
import { drawStandardHeaderAndFooter } from "app/core/components/pdf/pdf.component";
import { displayProductNumber } from "app/core/pipes/product-number.pipe";
import jsPDF from "jspdf";
import autoTable, { MarginPaddingInput, RowInput, Styles, UserOptions } from "jspdf-autotable";
import { isNumber } from "lodash";
import { forkJoin, mapTo, Observable, of, shareReplay, tap } from "rxjs";
import { StepperSelectionEvent } from "@angular/cdk/stepper";

@Component({
    selector: 'quote-wizard-dialog',
    templateUrl: './quote-wizard-dialog.component.html',
    styleUrls: ['./quote-wizard-dialog.component.scss'],
    standalone: false
})
export class QuoteWizardDialogComponent extends Paging implements OnInit
{
    /** Uploaded letterhead (Settings > Emails & PDFs) for this screen's printed header. Its footer
     *  is the LANDSCAPE artwork, which is not editable yet, so that one stays on the shipped file.
     *  inject() rather than a constructor argument, so the constructor signature is untouched. */
    readonly branding = inject(DocumentBrandingService);

    public dealId: number;
    public deal: DealDto;
    // All revisions of this quote (original + a, b, ...), oldest-first. Populated
    // for existing quotes so the header "Revisions" menu can list/open the earlier
    // revisions the kanban hides (it only shows IsLatestRevision == true).
    public revisions: DealRevisionSummaryDto[] = [];
    public customerForm: FormGroup;
    public productsForm: FormGroup;
    public summaryForm: FormGroup;
    
    public files: File[] = [];
    public fileInfos: QuoteFileDto[] = [];

    public isLinear: boolean = true;
    public selectedStepIndex = 0;

    public purchaseOrders: PurchaseOrderDto[] = [];
    public allUsers: UserDto[] = [];
    public parts: PartResponseWithSubstitutes[] = [];
    public filteredParts: PartResponseWithSubstitutes[] = [];
    public partFiles: PartFileDto[] = [];
    public products: DlmProductDto[] = [];
    public filteredProducts: DlmProductDto[] = [];
    public loadcells: LoadcellResponse[] = [];
    public filteredLoadcells: LoadcellResponse[] = [];
    public assets: ProductDto[] = [];
    public filteredAssets: ProductDto[] = [];
    public marineParts: MarinePartDto[] = [];
    public filteredMarineParts: MarinePartDto[] = [];
    public marinePartCategories: string[] = [];
    public filteredMarinePartCategories: string[] = [];

    // Slim {id,name} list for the picker. Full detail (invoice addresses) is
    // fetched on selection into selectedCompany; contacts are fetched per company.
    public companies: CustomerListItemDto[] = [];
    public filteredCompanies: CustomerListItemDto[] = [];
    selectedCompanyName: string = '';
    public selectedCompany: CustomerResponse = null;
    public contacts: CustomerContactListItemDto[] = [];
    public stages: DealStageDto[] = [];

    // True when the dialog was opened to amend a Negotiation-stage custom quote's
    // BOM (board passes data.editBom). Jumps to the BOM step and shows the
    // "Save BOM Revision" action; the BOM save then routes through the revision flow.
    public editBomMode = false;

    // Signature of the editable BOM content (items + assembly processes) captured
    // when the BOM loads. "Save BOM Revision" compares against it to avoid creating
    // a no-op revision when nothing changed.
    private loadedBomContentSig: string | null = null;

    public manufactureProcesses : ManufactureProcessCategoryBreakdownDto[] = [];
    public selectedManufactureCategoryId: number;
    public selectedManufactureProcessId: number;
    public selectedProcess: ManufactureProcessDto;
    public boms: BomDto[] = [];

    public appInfo: ApplicationInfoDto;

    // Markup config from /settings/markups — the source of truth for the
    // default charge-margin %. Loaded in the ngOnInit forkJoin so it's present
    // before buildForm seeds the summary form's margin control.
    public markupConfig: ProductMarkupConfigDto;

    // Sales categories, already in Dave's display order (Standard, Repeat Custom, Custom, ...) and
    // already minus the purchase-only ones: a quote can never be Misc (see DealHelperService).
    public categories = this.dealHelper.getCategoryOptions();
    public incoterms = Object.values(Incoterm).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getIncotermName(+x), value: <Incoterm>x});
    public freightTerms = Object.values(FreightTerm).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getFreightTermName(+x), value: <FreightTerm>x});
    public transportModes = Object.values(TransportMode).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getTransportModeName(+x), value: <TransportMode>x});
    public leadTimeTypes = Object.values(LeadTimeType).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getLeadTimeTypeName(+x), value: <LeadTimeType>x});
    
    public slaDate: Date = null;
    public now: Date = new Date();

    // Margin options seed their % from /settings/markups. A NEW quote opens on
    // Charge Margin % (2) carrying the rate its own type earns: see
    // getDefaultMarginPercent for why custom and standard differ. The chosen % is
    // applied as a markup on the line sale price → selling = base × (1 + margin/100).
    //   3 = Standard Margin (settings.standardMarkup — single field, readonly)
    //   4 = Custom Margin   (settings.customMarkup   — single field, readonly)
    //   2 = Charge Margin % (the FULLY-LOADED charge, compound not summed:
    //                        28/20/24/0/15 → ((1+0.72)×(1+0.15)-1)×100 = 97.8%,
    //                        pre-filled but editable so it can be fine-tuned)
    public marginTypes: EnumViewModel[] = [
        { value: 3, name: 'Standard Margin' },
        { value: 4, name: 'Custom Margin' },
        { value: 2, name: 'Charge Margin %' },
    ];

    public paymentTerms: { value: string, name: string }[] = [
        { value: '30 Days',                 name: '30 Days' },
        { value: '60 Days End of Month',    name: '60 Days End of Month' },
        { value: '60 Days',                 name: '60 Days' },
        { value: 'Proforma (In advance)',   name: 'Proforma (In advance)' },
        { value: '45 Days',                 name: '45 Days' },
        { value: '45 Days End of Month',    name: '45 Days End of Month' },
    ];

    public marginValues: number[] = [0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5];
    
    public vatRates: EnumViewModel[] = [
        { value: 0, name: '0% - Zero % Rated' },
        { value: 0.05, name: '5% - Reduced Rate' },
        { value: 0.2, name: '20% - Standard Rate' },
    ];

    public DealType = DealType;
    public ComponentSourceType = ComponentSourceType;

    // Tracks the typed-but-not-matched text in the part autocomplete so the "(Create New)..."
    // option can offer to seed a new draft Part with that number.
    public newPartName: string = '';

    // Part IDs created via the "(Create New)..." flow during this dialog session.
    // Used to suppress the zero-stock quote dialog for brand-new parts on every code path
    // (initial add, qty change, substitute swap) — a fresh part trivially has no stock and
    // no supplier history to quote against, so the dialog is just noise.
    private newlyCreatedPartIds: Set<number> = new Set();

    public readonly SUBCONTRACT_SETUP_WEEK_TO_HOURS = 35;

    // Item 1 — per-line process expansion. Keyed by bomItemId (positive) or clientLineId
    // (negative) so unsaved lines also get a stable identity. Mirrors BOM Builder.
    public expandedProcessRows = new Set<number>();
    private clientLineIdCounter = 0;
    private nextClientLineId(): number { return --this.clientLineIdCounter; }

    // Item 1 — sub-BOM linking, keyed by partId so unsaved items resolve too.
    // null cached value means "confirmed-none". Lazy-loaded on first ask.
    public linkedSubBomsByPartId: { [partId: number]: BomDto | null } = {};
    private linkedSubBomLoadingForPartId = new Set<number>();

    constructor(
        public dialogRef: MatDialogRef<QuoteWizardDialogComponent>,
        private dealHelper: DealHelperService,
        private partService: PartService,
        private repairService: RepairsAndCalibrationsService,
        private purchaseOrderService: PurchaseOrderService,
        private pickSheetService: PickSheetService,
        private dlmProductService: DlmProductService,
        private assetService: ProductService,
        private customerService: CustomerService,
        private dealService: DealService,
        private marineService: MarineService,
        private employeeService: EmployeeService,
        private bomService: BomService,
        private settingsService: SettingsService,
        private manufactureProcessService: ManufactureProcessService,
        private markupConfigService: ProductMarkupConfigService,
        private costCentreService: CostCentreService,
        private operationCodeService: OperationCodeService,
        private _formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private datePipe: DatePipe,
        private currencyPipe: CurrencyPipe,
        private dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _infoDialogService: GenericInfoDialogService,
        private _snackBar: MatSnackBar,
        private loader: AppLoaderService,
        private appAuth: AppAuthService,
        private router: Router,
        @Inject(MAT_DIALOG_DATA) public data: {deal: DealDto, editBom?: boolean}
    )
    {
        super();
    }

    ngOnInit(): void
    {
        // (The manual "move Repeat Custom to position 2" reorder that used to live here is gone:
        //  DealHelperService.categoryDisplayOrder is now the single source of that ordering, and
        //  this splice inserted an undefined entry whenever the value was missing from the list.)

        this.dealId = this.data.deal?.dealId ?? null;
        this.deal = this.data.deal ?? null;

        // Opened from the board specifically to amend a Negotiation-stage custom
        // quote's BOM. Drives the jump to the BOM step + the edit-mode save action.
        this.editBomMode = !!this.data.editBom;

        this.searchTerm = '';
        this.page.totalPages = 2;

        this.loader.open();
        // Open-time loads = step-1 (Customer Details) essentials only. The heavy
        // BOM-step datasets (parts ~2MB, products ~0.7MB, loadcells, assets, marine,
        // manufacture) are deferred to ensureBomDataLoaded() — fired when the user
        // reaches the BOM step, or up-front for existing deals (loadDeal reads them).
        forkJoin({
            c: this.loadCompanies(),
            e: this.loadUsers(),
            f: this.loadStages(),
            g: this.loadAppInfo(),
            l: this.loadMarkupConfig(),
        }).subscribe({
            next: () => {
                this.loader.close();
                if (this.dealId) {
                    // Existing quote: loadDeal reads products/manufacture/parts (and
                    // custom quotes jump straight to the BOM step), so load the BOM
                    // datasets before it runs.
                    this.loadRevisions();
                    this.ensureBomDataLoaded().subscribe(() => this.loadDeal());
                }
                else
                    this.loadDeal();
            },
            error: () => {
                this.loader.close();
            }
        });

        // this.loadPurchaseOrders();
        this.loadCostCentres();
        this.loadOperationCodes();
        this.loadPartLocations();
    }

    private bomData$?: Observable<void>;

    /**
     * Lazily loads the BOM-step datasets (parts, products, loadcells, assets,
     * marine parts, manufacture processes) exactly once. These are heavy (~2.7MB)
     * and not needed to render step 1, so they're kept out of the open-time
     * forkJoin. shareReplay(1) means the step-change trigger and the existing-deal
     * load share a single fetch rather than firing it twice.
     */
    ensureBomDataLoaded(): Observable<void> {
        if (!this.bomData$) {
            this.bomData$ = forkJoin({
                a: this.loadParts(),
                b: this.loadProducts(),
                h: this.loadManufactureProcesses(),
                i: this.loadLoadcells(),
                j: this.loadAssets(),
                k: this.loadMarineParts(),
            }).pipe(mapTo(void 0), shareReplay(1));
        }
        return this.bomData$;
    }

    onStepChange(event: StepperSelectionEvent) {
        // Step 0 = Customer Details. Reaching any later step needs the BOM datasets.
        if (event.selectedIndex > 0)
            this.ensureBomDataLoaded().subscribe();
    }

    // Bin-typed leaves only, sorted by name. Used by the create-new-part
    // popup's Storage Location dropdown so new parts pin to a registered bin
    // rather than free text. Mirrors bom-builder's loadPartLocations.
    loadPartLocations() {
        this.partService.apiPartLocationsGet$Json$Response().subscribe(res => {
            if (!res?.body?.data) return;
            this.partLocations = sortOnProperty(res.body.data.filter(x => x.locationType === PartLocationType.Bin), 'name');
        });
    }

    loadCompanies() {
        // Slim {id,name} list — see CustomerListItemDto. Avoids the ~3MB full
        // CustomerResponse graph; full detail is fetched on selection.
        return this.customerService.apiCustomerCompaniesListGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.companies = res.body.data;
            this.filteredCompanies = this.companies;
        }),
        mapTo(void 0));
    }

    /**
     * Fetches a single company's full detail (invoice addresses) + that company's
     * contacts. Replaces the old eager all-companies/all-contacts loads — fired on
     * company selection and on existing-deal load.
     */
    loadSelectedCompany(companyId: number): Observable<void> {
        if (!companyId) {
            this.selectedCompany = null;
            this.contacts = [];
            return of(void 0);
        }
        return forkJoin({
            company: this.customerService.apiCustomerCustomerGet$Json$Response({ id: companyId }),
            contacts: this.customerService.apiCustomerSearchContactsGet$Json$Response({ companyId }),
        }).pipe(tap(({ company, contacts }) => {
            this.selectedCompany = company?.body?.data ?? null;
            this.contacts = contacts?.body?.data ?? [];
            if (this.selectedCompany?.name) {
                this.selectedCompanyName = this.selectedCompany.name;
                this.regenTitle();
            }
            // Default the invoice address / VAT once the company's full detail
            // (with its invoice addresses) has arrived. Covers both opening an
            // existing quote and picking a company anew; the guard inside leaves
            // any already-selected / deal-loaded address untouched.
            this.applyDefaultInvoiceAddress();
            this.cdr.detectChanges();
        }),
        mapTo(void 0));
    }

    loadStages() {
        return this.dealService.apiDealStagesGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.stages = res.body.data;
        }),
        mapTo(void 0));
    }

    loadDeal() {
        if(!this.dealId) {
            this.dealService.apiDealNextNonWonQuoteRefGet$Json$Response().subscribe(res => {
                if(!res?.body?.message) return;
                this.deal = { quoteRef: res.body.message };
                this.regenTitle();
            });

            this.buildForm();
            return;
        }

        this.dealService.apiDealDealGet$Json$Response({dealId: this.dealId}).subscribe(res => {
            if(!res.body.data) return;

            this.deal = res.body.data;

            // Load this deal's company detail (invoice addresses) + contacts —
            // replaces the old eager all-companies/all-contacts loads.
            this.loadSelectedCompany(this.deal.companyId).subscribe();

            //calculate SLA date
            this.calculateSLA();

            //pull in BOM
            if(this.deal.bomId) {
                this.bomService.apiBomBomGet$Json$Response({bomId: this.deal.bomId}).subscribe(res => {
                    if(!res?.body?.data) return;
                    // Defer to next macrotask so the state mutation + the
                    // resulting @for row creation never run inside the host
                    // dialog's CD pass. The dialog is mounted async, and
                    // assembly process rows render with 'Assembly' display
                    // text (via getCostCentreName/getOperationName lookups);
                    // populating those rows mid-CD trips NG0100 every time.
                    setTimeout(() => {
                        this.bom = res.body.data;
                        // Translate American-spelled BomAssemblyProcess fields
                        // (costCenterCode/costCenterRate) into the British spelling
                        // (costCentreCode/costCentreRate) every template binding +
                        // helper reads from. Mirror of bom-builder.putResToBom.
                        this.bomAssemblyProcesses = ((this.bom as any).assemblyProcesses || []).map((p: any) => ({
                            ...p,
                            costCentreCode: p.costCentreCode ?? p.costCenterCode,
                            costCentreRate: p.costCentreRate ?? p.costCenterRate,
                        }));
                        // Baseline of the editable BOM content (items + assembly
                        // processes) so "Save BOM Revision" can skip making a no-op
                        // revision when nothing was actually changed.
                        this.loadedBomContentSig = this.bomContentSignature(this.bom.items, this.bomAssemblyProcesses);
                        this.customProductName = this.lookupProductById(this.bom.productId)?.description ?? '';
                        this.customProductPartNumber = this.lookupProductById(this.bom.productId)?.partNumber ?? '0001-9999';

                        // Resolve sub-BOMs + drawings for every line so the per-line expansion can render eagerly.
                        this.loadLinkedSubBoms();
                        const partIds = getUniqueOnSelf((this.partItems?.controls ?? []).map((c: any) => c.get('partId')?.value));
                        for (const pid of partIds) this.loadPartFiles(pid);
                    }, 0);
                });
            }

            // this should only happen for legacy quotes with no ref
            if(!this.deal.quoteRef)
                this.dealService.apiDealQuoteRefGet$Json$Response({dealId: this.dealId}).subscribe(res => {
                    if(!res?.body?.message) return;
                    this.deal.quoteRef = res.body.message;
                    this.regenTitle();
                });

            //load process and set selected values
            if(this.deal.manufactureColumnDefinitionId) {
                this.loadChosenManufactureProcess(this.deal.manufactureColumnDefinitionId);
                
                //set selected values
                this.selectedManufactureProcessId = this.manufactureProcesses
                    .flatMap(x => x.processes)
                    .find(x => x.columnDefinitions.some(c => c.manufactureProcessColumnDefinitionId == this.deal.manufactureColumnDefinitionId))?.manufactureProcessId;
                this.selectedManufactureCategoryId = this.manufactureProcesses
                    .find(x => x.processes.some(p => p.manufactureProcessId == this.selectedManufactureProcessId))?.manufactureProcessCategoryId;
            }
        
            this.buildForm();
            this.pageProducts();
            this.loadFiles();

            //if this is an unfinished custom quote (or we're amending a Negotiation-stage
            //custom quote's BOM), jump straight to the BOM step
            if((this.deal.isWizardInProgress || this.editBomMode) && this.deal.isCustomQuote && !this.deal.isWizardInFirstStage) {
                this.isLinear = false;
                this.selectedStepIndex = 2;
            }
        });
    }

    loadPurchaseOrders() {
        this.purchaseOrderService.apiPurchaseOrderGetAllGet$Json$Response({isSupplier: false}).subscribe(res => {
            if(!res?.body?.data) return;
            
            this.purchaseOrders = res.body.data.filter(x => !x.isDraft);
            this.cdr.detectChanges();
        });
    }

    loadOperationCodes() {
        this.operationCodeService.apiOperationCodeOperationCodesGet$Json$Response().subscribe((res) => {
            if (!res?.body?.data) return;

            this.operationCodes = res.body.data;
            queueMicrotask(() => this.cdr.detectChanges());
        });
    }

    loadCostCentres() {
        this.costCentreService.apiCostCentreCostCentresGet$Json$Response().subscribe((res) => {
            if (!res?.body?.data) return;

            this.costCentres = res.body.data;
            // Defer to next microtask so we never nest a CD inside an
            // ongoing one (HTTP interceptor caches can deliver this
            // subscription synchronously during the host's CD pass).
            queueMicrotask(() => this.cdr.detectChanges());
        });
    }
    
    loadUsers() {
        return this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response({archived: false}).pipe(tap(res => {
            if(!res?.body.data) return;
            this.allUsers = res.body.data;
        }),
        mapTo(void 0));
    }

    public loadAppInfo() {
        return this.settingsService.apiSettingsAppInfoGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.appInfo = res.body.data;
        }),
        mapTo(void 0));
    }

    // Charge-margin defaults live in /settings/markups. Mirror of bom-builder's
    // loadMarkup so the wizard's seeded margin matches the BOM Builder's sell price.
    loadMarkupConfig() {
        return this.markupConfigService.apiProductMarkupConfigGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.markupConfig = res.body.data;
        }),
        mapTo(void 0));
    }

    loadProducts() {
        // includeDrafts: same rationale as loadParts — products created from a not-yet-won
        // quote remain selectable for that quote until activation on win.
        return this.dlmProductService.apiDlmProductGetProductsGet$Json$Response({includeDrafts: true}).pipe(tap(res => {
            if (!res?.body?.data) return;

            this.products = res.body.data;
            this.pageProducts();
        }),
        mapTo(void 0));
    }

    loadParts(includeSubstitutes: boolean = true) {
        // includeDrafts: the wizard needs to see draft parts created from this or another
        // not-yet-won quote so they remain selectable until activation on win.
        return this.partService.apiPartPartsGet$Json$Response({ includeAvailability: false, includeSubstitutes, includeDrafts: true }).pipe(tap(res => {
            if(!res?.body?.data) return;
            this.parts = res.body.data;

            //no longer paged after switching to BOM view for custom products
            // this.pageParts();
            this.filteredParts = this.parts;
            
            // refresh form array values so displayWith starts working
            const fa = this.partItems;
            fa?.setValue(fa.value);
            fa?.updateValueAndValidity({ emitEvent: false });
            // Defer to next microtask: the setValue trick above can trigger
            // valueChanges on autocomplete fields, and forcing CD synchronously
            // here creates child views mid-cycle if the subscription happens
            // to land during the host's own CD pass.
            queueMicrotask(() => this.cdr.detectChanges());
        }),
        mapTo(void 0));
    }

    loadLoadcells() {
        return this.repairService.apiRepairsAndCalibrationsLoadcellsGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.loadcells = sortOnProperty(res.body.data, 'bookingRef');
            this.pageLoadcells();
        }),
        mapTo(void 0));
    }

    loadAssets() {
        return this.assetService.apiProductGetAllGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.assets = sortOnProperty(res.body.data, 'name');
            this.pageAssets();
        }),
        mapTo(void 0));
    }

    /** Legacy: the V1 wizard's marine list is the Marine Part Register (main register parts flagged marine,
     *  DLM 2026-09-29). A register part has no free-text category, so the category list is only what the
     *  deal's own lines already carry. */
    loadMarineParts() {
        return this.marineService.apiMarinePartsGet$Json$Response({ PageIndex: 0, PageSize: 500 }).pipe(tap(res => {
            if(!res?.body?.data?.page?.items) return;

            this.marineParts = res.body.data.page.items;
            this.filteredMarineParts = this.marineParts;
            this.marinePartCategories = getUniqueOnSelf((this.deal?.quoteMarineLines ?? []).map(x => x.category).filter(x => !!x));
            this.filteredMarinePartCategories = this.marinePartCategories;
        }),
        mapTo(void 0));
    }

    loadManufactureProcesses() {
        return this.manufactureProcessService.apiManufactureProcessGetCategoriesBreakdownGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.manufactureProcesses = res.body.data;
        }),
        mapTo(void 0));
    }
    
    loadChosenManufactureProcess(columnDefinitionId: number) {
        if(!columnDefinitionId) return;

        //fetch the process
        this.manufactureProcessService.apiManufactureProcessGetProcessGet$Json$Response({processId: this.selectedManufactureProcessId}).subscribe(res => {
            if(!res?.body?.data) return;

            var process = res.body.data;
            process.columnDefinitions = null;

            //get steps specifically for the column chosen
            for(var stage of process.stages)
                for(var step of stage.steps) {
                    step['value'] = step.values.find(x => x.columnDefinitionId == columnDefinitionId)?.value;
                    step.values = null;
                }

            this.selectedProcess = process;
        });
    }

    getBomForProduct(productId: number) {
        var bom = this.boms.find(x => x.productId == productId);

        if(bom)
            return of(null);
        else
            return this.bomService.apiBomBomByProductIdGet$Json$Response({productId: productId}).pipe(tap(res => {
                    if(!res?.body?.data) return null;
                    this.boms.push(res.body.data);
                },
            ),
            mapTo(void 0));
    }
    
    getManufactureProcesses = () => this.manufactureProcesses?.find(x => x.manufactureProcessCategoryId == this.selectedManufactureCategoryId)?.processes ?? [];
    getManufactureColumns = () => this.getManufactureProcesses()?.find(x => x.manufactureProcessId == this.selectedManufactureProcessId)?.columnDefinitions ?? [];
    getManufactureProcessTotal = () => sumList(this.selectedProcess?.stages.flatMap(stage => sumList(stage.steps.map(x => +x['value']))) ?? []);
    getHoursText = (x: number) => TimeUtil.getHoursText(x);

    manufactureCategoryChanged(event: any) {
        this.selectedManufactureCategoryId = event.value;
        this.selectedManufactureProcessId = null;
        this.selectedProcess = null;
        this.customerForm.controls['manufactureColumnDefinitionId'].setValue(null);
    }
    manufactureProcessChanged(event: any) {
        this.selectedManufactureProcessId = event.value;
        this.selectedProcess = null;
        this.customerForm.controls['manufactureColumnDefinitionId'].setValue(null);
    }


    pageProducts(setManualRes: boolean = false) {
        // Search number OR description. Number-only matched nothing for a description word
        // like "shear", even though the server's GetProducts searches both. Note the
        // isCustomProduct gate: Standard Product shows only register products, Repeat
        // Custom only previously-built ones, so a miss here is often the Order Type.
        const term = (this.searchTerm ?? '').toLowerCase();
        var searchFiltered = this.products.filter(m => this.isRepeatCustomProduct() == m.isCustomProduct
            && (m.partNumber?.toLowerCase().includes(term) || m.description?.toLowerCase().includes(term)));
        this.filteredProducts = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);

        // Page info comes off the FILTERED set, not the whole register — otherwise a search
        // that narrows to a handful still reports the full total and infinite scroll keeps
        // bumping pageIndex against rows that are no longer there.
        if(setManualRes)
            this.setPageInfoFromManualRes(searchFiltered);
    }

    pageParts(setManualRes: boolean = false) {
        var searchFiltered = this.parts.filter(m => m.number?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredParts = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.parts);
    }

    pageLoadcells(setManualRes: boolean = false) {
        var searchFiltered = this.loadcells.filter(m => m.serialNumber?.toLowerCase().includes(this.searchTerm.toLowerCase()) || m.bookingRef?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredLoadcells = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.loadcells);
    }

    pageAssets(setManualRes: boolean = false) {
        var searchFiltered = this.assets.filter(m => m.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) || m.serialNumber?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredAssets = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.assets);
    }
    
    onScroll(event: any) {
        if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 2) { // -2 is for cross-browser issue(?) where in rare cases scroll isn't detected but only on Mitch's chrome
            if(this.page.pageIndex < this.page.totalPages && this.page.totalPages > 1) {
                this.page.pageIndex++;

                if(this.isCustomQuote())
                    this.pageParts(true);
                else if (this.getDealType() == DealType.Calibration)
                    this.pageLoadcells(true);
                else if (this.getDealType() == DealType.Hire)
                    this.pageAssets(true);
                else
                    this.pageProducts(true);
            }
        }
    }
    
    // New-quote default for the Expected Close Date field — today + 30 days.
    // Returns null when editing an existing deal so we don't override values
    // the user (or backend activation flow) has already chosen.
    private getDefaultExpectedCloseDate(): Date | null {
        if (this.dealId) return null;
        const d = new Date();
        d.setDate(d.getDate() + 30);
        return d;
    }

    buildForm() {
        this.customerForm = this._formBuilder.group({
            name                : [this.deal?.name ?? '',               Validators.required],
            customerReqNo       : [this.deal?.customerReqNo ?? '',      Validators.required],
            // stageId             : [this.item?.stageId ?? '',            Validators.required],
            // sentDate            : [this.item?.sentDate ?? ''],
            // closeDate           : [this.deal?.closeDate ?? ''],
            companyId           : [this.deal?.companyId ?? null,          Validators.required],
            contactId           : [this.deal?.contactId ?? null,          Validators.required],
            invoiceAddressId    : [this.deal?.invoiceAddressId ?? null],
            
            // value               : [this.item?.value ?? '',              Validators.required],
            // partsDescription    : [this.item?.partsDescription ?? ''],
            dealCategories      : [this.deal?.dealCategories ?? []],
            // comments            : [this.deal?.comments ?? ''],
            // userId              : [this.deal?.userId ?? null,             Validators.required],

            isCustomQuote           : [this.deal?.isCustomQuote ?? false],
            isRepeatCustomProduct   : [this.deal?.isRepeatCustomProduct ?? false],
            isConsignmentStock      : [this.deal?.isConsignmentStock ?? false],
            dealType                : [this.deal?.dealType ?? DealType.Standard],

            expectedCloseDate   : [this.deal?.expectedCloseDate ?? this.getDefaultExpectedCloseDate(), Validators.required],
            notes               : [this.deal?.notes ?? ''],

            manufactureColumnDefinitionId : [this.deal?.manufactureColumnDefinitionId ?? null],
        });

        this.productsForm = this._formBuilder.group({
            productLines        : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => [DealType.Standard, DealType.RepeatCustom].includes(this.getDealType()))),
            partLines           : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.isCustomQuote())),

            calibrationLines    : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Calibration)),
            hireLines           : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Hire)),
            marineLines         : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Marine)),
        });
        
        // marginType is UI-only (not persisted on the deal). A new quote opens on
        // the Standard Margin preset (3), seeded from /settings/markups. A reopened
        // quote keeps its saved % and we derive which dropdown entry to show: the
        // matching preset if the saved % equals the standard/custom markup, else
        // manual 'Charge Margin %' (2). We never silently overwrite a quoted price.
        const hasSavedMargin = this.deal?.margin != null;
        const savedMargin = this.deal?.margin;
        // A NEW quote opens on Charge Margin % (2) so it carries the fully-loaded rate
        // its own type earns, and stays editable. It used to open on the Standard
        // preset (3) = standardMarkup alone = 15%, which under-quoted every custom job
        // by ~59% because a custom total is a RAW-COST build-up that nothing else loads.
        const defaultMarginType =
            !hasSavedMargin                                  ? 2 :
            savedMargin === this.getStandardMarkupPercent()  ? 3 :
            savedMargin === this.getCustomMarkupPercent()    ? 4 :
                                                               2;
        this.summaryForm = this._formBuilder.group({
            marginType          : [defaultMarginType],
            // No hard floor — the default comes from settings and is applied as a
            // markup on the line sale price (selling = base × (1 + margin/100)).
            // A REOPENED quote keeps its saved %, so a price already sent to a
            // customer is never silently restated.
            margin              : [hasSavedMargin ? savedMargin : this.getDefaultMarginPercent(), [(c: FormGroup) => this.singleValidator(c, () => this.hasMargin(), () => this.marginNonNegative())]],
            incoterm            : [this.deal?.incoterm ?? null,         Validators.required],
            freightTerm         : [this.deal?.freightTerm ?? null],
            transportMode       : [this.deal?.transportMode ?? null],
            freightCost         : [this.deal?.freightCost ?? 0],
            // Default 20% (standard rate) for new quotes; ?? keeps a saved deal's
            // value — including an explicit 0% — untouched.
            vatRate             : [this.deal?.vatRate ?? 0.2,           Validators.required],
            paymentTerm         : [this.deal?.paymentTerm ?? null],
        });

        this.deal?.quoteProductLines?.forEach((item: DealQuoteProductLineDto) => this.addProductItem(item));
        this.deal?.quotePartLines?.forEach((item: DealQuotePartLineDto) => this.addPartItem(item));
        this.deal?.quoteCalibrationLines?.forEach((item: DealQuoteCalibrationLineDto) => this.addCalibrationItem(item));
        this.deal?.quoteHireLines?.forEach((item: DealQuoteHireLineDto) => this.addHireItem(item));
        this.deal?.quoteMarineLines?.forEach((item: DealQuoteMarineLineDto) => this.addMarineItem(item));

        // Name comes from the slim list (loaded up-front); full detail loads async.
        if(this.deal?.companyId && this.companies?.length)
            this.selectedCompanyName = this.companies.find(x => x.customerId == this.deal.companyId)?.name;

        this.cdr.detectChanges();
    }

    hasMargin = () => this.summaryForm?.controls['marginType'].value > 0;
    // Replaces the old >= 15 hard floor. The margin default now comes from the
    // markup presets (getStandardMarkupPercent / getCustomMarkupPercent); we
    // only guard against negatives.
    marginNonNegative = () => (this.summaryForm?.controls['margin'].value || 0) >= 0;

    itemsValidator(control: FormArray, requiredFn: () => boolean): { [key: string]: boolean } | null {
        return (requiredFn() && control.length < 1) ? { 'noItems': true } : null;
    }
    
    singleValidator(control: FormGroup, requiredFn: () => boolean, validFn: () => boolean): { [key: string]: boolean } | null {
        return (requiredFn() && !validFn()) ? { 'invalidItem': true } : null;
    }

    get productItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('productLines') as FormArray : null;
    }
    get partItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('partLines') as FormArray : null;
    }
    get calibrationItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('calibrationLines') as FormArray : null;
    }
    get hireItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('hireLines') as FormArray : null;
    }
    get marineItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('marineLines') as FormArray : null;
    }

    // Item 1 (custom quote modal parity with BOM Builder Step 2) — port of helpers
    // from bom-builder.component.ts. Same names + semantics so future component
    // extraction is a copy-paste rather than a rename pass.
    isZeroCost(itemControl: AbstractControl): boolean {
        const cost = (itemControl as FormGroup)?.controls?.['cost']?.value ?? 0;
        return cost <= 0;
    }
    getZeroCostLineCount(): number {
        if (!this.isCustomQuote()) return 0;
        let count = 0;
        this.partItems?.controls?.forEach((ctrl: AbstractControl) => {
            if (this.isZeroCost(ctrl)) count++;
        });
        return count;
    }
    hasZeroCostLines(): boolean {
        return this.getZeroCostLineCount() > 0;
    }

    // Mirror of BomBuilder.getItemRolledUpTotal — own subtotal + sum of every descendant
    // identified by dot-notation level prefix.
    getItemRolledUpTotal(itemControl: FormGroup): number {
        const myLevel: string = itemControl?.controls?.['level']?.value ?? '';
        let total = this.getItemSubtotal(itemControl);
        if (!myLevel) return total;
        const prefix = myLevel + '.';
        this.partItems?.controls?.forEach((other: AbstractControl) => {
            if (other === itemControl) return;
            const otherLevel: string = (other as FormGroup)?.controls?.['level']?.value ?? '';
            if (otherLevel.startsWith(prefix)) {
                total += this.getItemSubtotal(other as FormGroup);
            }
        });
        return total;
    }

    // Synthetic row 0 — uses the deal's custom product name (already tracked
    // server-side in customProductName) instead of firstFormGroup.partNumber.
    hasProductRow(): boolean {
        return this.isCustomQuote() && !!this.customProductName;
    }
    getProductRowLabel(): string {
        return this.customProductName ? `${this.customProductName} (Product)` : '';
    }

    // Fires the Record Part Quote dialog when an existing part needs sourcing attention.
    // Two triggers, either is enough:
    //   1. Missing unit cost (cost == 0 or null) — the line would otherwise quote at £0.
    //   2. Insufficient stock for the requested quantity (skipped for isNonStock parts,
    //      which have no tracked stock by definition).
    // Suppressed for parts created in this session, since brand-new parts have no
    // supplier history or stock yet and the dialog would be noise.
    warnIfNeedsPartQuote(part: PartResponse, qtyRequired: number, itemControl?: FormGroup) {
        if (!part) return;
        if (this.newlyCreatedPartIds.has(part.id)) return;

        // A Manual cost part (soft-launch item 1) is priced per LINE at the day's price, typed on
        // the line itself, so a missing register cost is not a reason to record a part quote.
        const manualCost = !!part.usesManualCost;
        const hasCost = manualCost || (part.cost ?? 0) > 0;
        const stockOk = part.isNonStock || (part.currentStock ?? 0) >= (qtyRequired ?? 1);
        if (hasCost && stockOk) return;

        const ref = this.dialog.open(RecordPartQuoteDialogComponent, {
            data: {
                partId: part.id,
                partNumber: part.number,
                currentCost: part.cost,
            },
            autoFocus: false,
        });
        ref.afterClosed().subscribe(result => {
            if (!result) return;
            // The dialog recorded a new quote server-side, which raises the part's cost
            // when the quote is higher than the current cost (see the dialog's note).
            // Re-fetch parts so cost + stock reflect the save, THEN copy the authoritative
            // cost back onto the triggering line — otherwise the line keeps the pre-dialog
            // £0 it was seeded with, so the quoted cost never reaches the saved quote line
            // or the BOM grid (buildModel persists itemControl.value.cost verbatim).
            // NB: loadParts() returns a COLD observable — it must be subscribed or the GET
            // never fires. The previous bare `this.loadParts();` was a silent no-op.
            this.loadParts().subscribe(() => {
                // Never overwrite a Manual cost line's day price with the register cost.
                if (!itemControl || manualCost) return;
                const refreshed = this.lookupPartById(part.id);
                if (!refreshed) return;
                itemControl.controls['cost'].setValue(refreshed.cost ?? 0);
                this.cdr.detectChanges();
            });
        });
    }
    checkLineStock(itemControl: FormGroup) {
        const partId = itemControl?.controls?.['partId']?.value;
        const qty = itemControl?.controls?.['quantity']?.value ?? 1;
        if (!partId) return;
        const part = this.lookupPartById(partId);
        if (!part) return;
        this.warnIfNeedsPartQuote(part, qty, itemControl);
    }

    // Item 1 — full BOM Builder Step 2 parity for Custom Product quotes.
    // The block below mirrors bom-builder.component.ts; same names + semantics so a
    // future shared component extraction is mostly a copy-paste.

    // Stable identity for a quote line whether saved or not. Positive = real bomItemId,
    // negative = client-side correlation id used to bind processes pre-save.
    getLineKey(itemControl: FormGroup | null | undefined): number | null {
        if (!itemControl) return null;
        const realId = itemControl.get('bomItemId')?.value as number | null;
        if (realId && realId > 0) return realId;
        const clientId = itemControl.get('clientLineId')?.value as number | null;
        return clientId ?? null;
    }

    toggleLineProcesses(lineKey: number | null | undefined): void {
        if (!lineKey) return;
        if (this.expandedProcessRows.has(lineKey)) this.expandedProcessRows.delete(lineKey);
        else this.expandedProcessRows.add(lineKey);
        this.cdr.detectChanges();
    }

    isLineProcessesExpanded(lineKey: number | null | undefined): boolean {
        return !!lineKey && this.expandedProcessRows.has(lineKey);
    }

    private processMatchesLineKey(p: any, lineKey: number | null): boolean {
        if (lineKey == null) return p.bomItemId == null && p.clientLineId == null;
        if (lineKey > 0) return p.bomItemId === lineKey;
        return p.clientLineId === lineKey;
    }

    // Memoise the filtered+sorted list per lineKey so [cdkDropListData], @if
    // and @for all see the SAME array reference within a CD pass. Without
    // this, .filter().sort() returns a fresh outer array on every call and
    // every CD tick — which under A21 spams NG0100 because Angular sees
    // child-view bindings flip undefined→value on the verification pass.
    // Cache key: bomAssemblyProcesses identity + length + an explicit bump
    // for reorders (which mutate in place without changing length).
    private _processesCache: Map<number | null, any[]> | null = null;
    private _processesCacheSrc: any[] | null = null;
    private _processesCacheLen: number = -1;
    private _processesCacheBump: number = 0;
    private _processesBumpCounter: number = 0;

    private bumpProcessesCache() {
        this._processesBumpCounter++;
    }

    getProcessesForItem(lineKey: number | null): any[] {
        const src = this.bomAssemblyProcesses;
        if (
            !this._processesCache ||
            this._processesCacheSrc !== src ||
            this._processesCacheLen !== src.length ||
            this._processesCacheBump !== this._processesBumpCounter
        ) {
            this._processesCache = new Map();
            this._processesCacheSrc = src;
            this._processesCacheLen = src.length;
            this._processesCacheBump = this._processesBumpCounter;
        }
        const cached = this._processesCache.get(lineKey);
        if (cached) return cached;
        const result = src
            .filter(p => this.processMatchesLineKey(p, lineKey))
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        this._processesCache.set(lineKey, result);
        return result;
    }

    getAssemblyLevelProcesses(): any[] {
        return this.getProcessesForItem(null);
    }

    getProcessCountForItem(lineKey: number | null): number {
        return this.bomAssemblyProcesses.filter(p => this.processMatchesLineKey(p, lineKey)).length;
    }

    // Badge count on a line: own per-line steps + steps inherited from any linked sub-BOM.
    getTotalProcessCountForItem(itemControl: FormGroup): number {
        const local = this.getProcessCountForItem(this.getLineKey(itemControl));
        const sub = this.getLinkedSubBomProcesses(itemControl)?.length ?? 0;
        return local + sub;
    }

    getProcessLineKey(process: any): number | null {
        if (process?.bomItemId && process.bomItemId > 0) return process.bomItemId;
        if (process?.clientLineId && process.clientLineId < 0) return process.clientLineId;
        return null;
    }

    private getQuantityForProcess(process: any): number {
        const key = this.getProcessLineKey(process);
        if (!key) return 1;
        const itemCtrl = (this.partItems?.controls as FormGroup[])?.find(c => this.getLineKey(c) === key);
        return itemCtrl?.get('quantity')?.value ?? 1;
    }

    getBomItemDescriptor(lineKey: number | null): string {
        if (lineKey == null) return 'Assembly-level';
        const itemCtrl = (this.partItems?.controls as FormGroup[])?.find(c => this.getLineKey(c) === lineKey);
        if (!itemCtrl) return lineKey > 0 ? `Line ${lineKey}` : 'New line';
        const partId = itemCtrl.get('partId')?.value;
        const part = this.parts.find(p => p.id === partId);
        const level = itemCtrl.get('level')?.value;
        return part ? `${level} (${part.number})` : `Line ${level ?? lineKey}`;
    }

    // Sub-BOM linking — by partId so unsaved lines also resolve. Cache-aware: same
    // partId only fires one network request even if multiple lines reference it.
    loadLinkedSubBoms(): void {
        (this.partItems?.controls as FormGroup[] | undefined)?.forEach(c => {
            const partId = c.get('partId')?.value;
            this.ensureLinkedSubBomLoaded(partId);
        });
    }

    ensureLinkedSubBomLoaded(partId: number | null | undefined): void {
        if (!partId || partId <= 0) return;
        if (partId in this.linkedSubBomsByPartId) return;
        if (this.linkedSubBomLoadingForPartId.has(partId)) return;
        this.linkedSubBomLoadingForPartId.add(partId);

        this.bomService.apiBomLinkedSubBomByPartGet$Json$Response({ partId }).subscribe(res => {
            this.linkedSubBomLoadingForPartId.delete(partId);
            this.linkedSubBomsByPartId[partId] = res?.body?.data ?? null;
            this.cdr.detectChanges();
        });
    }

    getLinkedSubBomForItem(itemControlOrPartId: FormGroup | number | null | undefined): BomDto | null {
        let partId: number | null | undefined;
        if (itemControlOrPartId && typeof itemControlOrPartId === 'object' && 'get' in itemControlOrPartId) {
            partId = (itemControlOrPartId as FormGroup).get('partId')?.value;
        } else {
            partId = itemControlOrPartId as number | null | undefined;
        }
        if (!partId || partId <= 0) return null;
        if (!(partId in this.linkedSubBomsByPartId)) {
            this.ensureLinkedSubBomLoaded(partId);
            return null;
        }
        return this.linkedSubBomsByPartId[partId];
    }

    getLinkedSubBomProcesses(itemControlOrPartId: FormGroup | number | null | undefined): any[] {
        const sub = this.getLinkedSubBomForItem(itemControlOrPartId);
        const procs = (sub as any)?.assemblyProcesses ?? [];
        return [...procs].sort((a: any, b: any) => (a.sequence || 0) - (b.sequence || 0));
    }

    openSubBomInNewTab(subBomId: number | null | undefined): void {
        if (!subBomId) return;
        const url = this.router.createUrlTree(
            ['/product-register/bom-builder'],
            { queryParams: { bomId: subBomId } }
        ).toString();
        window.open(url, '_blank');
    }

    // Substitutes — list of alternative parts attached to the current line's part.
    getSubstitutes(itemControl: FormGroup): PartResponseWithSubstitutes[] {
        const part = this.lookupPart(itemControl) as PartResponseWithSubstitutes;
        return (part?.substitutePartIds || [])
            .map(id => this.parts.find(p => p.id === id))
            .filter(p => !!p) as PartResponseWithSubstitutes[];
    }

    isCheapestSubstitute(itemControl: FormGroup, part: PartResponseWithSubstitutes): boolean {
        const subs = this.getSubstitutes(itemControl);
        if (!subs.length) return false;
        const cheapest = subs.reduce((min, p) => (p.cost ?? 0) < (min.cost ?? 0) ? p : min, subs[0]);
        return cheapest?.id === part.id;
    }

    selectSubstitute(itemControl: FormGroup, part: PartResponseWithSubstitutes): void {
        itemControl.controls['partId'].setValue(part.id);
        itemControl.controls['cost'].setValue(part.cost ?? 0);
        const qty = itemControl.controls['quantity']?.value ?? 1;
        this.warnIfNeedsPartQuote(part, qty, itemControl);
        this.cdr.detectChanges();
    }

    // Drawings — files uploaded against a Part. Keyed by partId so multiple lines
    // with the same part share the same file list.
    getFilesForPart(itemControl: FormGroup): PartFileDto[] {
        const part = this.lookupPart(itemControl);
        if (!part) return [];
        return this.partFiles.filter(x => x.partId === part.id);
    }

    loadPartFiles(partId: number): void {
        if (!partId || this.partFiles.some(x => x.partId === partId)) return;
        this.partService.apiPartGetFilesGet$Json({ partId }).subscribe(res => {
            if (!res?.data) return;
            this.partFiles.push(...res.data);
            this.cdr.detectChanges();
        });
    }

    downloadPartFile(file: PartFileDto): void {
        this.partService.apiPartDownloadFileFileIdGet({ fileId: file.partFileId }).subscribe(res => {
            if (!res) return;
            const blob = new Blob([res], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            if (!file?.name.endsWith(file?.type)) file.name += file?.type;
            link.download = file.name;
            link.click();
            window.URL.revokeObjectURL(url);
        });
    }

    addProduct(product: DlmProductDto) {
        //get bom
        this.getBomForProduct(product.dlmProductId).subscribe(_ => {
            //get lead time
            var bom = this.boms.find(x => x.productId == product.dlmProductId);
            var leadTime = sumList(bom?.assemblyProcesses?.map(x => (x.setupHours ?? 0) * (x.isSubcontracted ? 35 : 1) + (x.runPerUnitHours ?? 0)) ?? []);

            this.addProductItem({
                productId: product.dlmProductId,
                cost: this.lookupProductById(product.dlmProductId)?.price,
                leadTime: TimeUtil.getWeeksNumber(leadTime),
                leadTimeType: LeadTimeType.Weeks,
            });
        });

    }
    addPart = (part: PartResponse) => {
        this.addPartItem({
            partId: part.id,
            cost: this.lookupPartById(part.id)?.cost,
        });
        // addPartItem pushes, so the just-added line is the last control. Pass it through
        // so a recorded quote's cost flows back onto this line (see warnIfNeedsPartQuote).
        const newLine = this.partItems?.at(this.partItems.length - 1) as FormGroup;
        // Newly-added line defaults to qty 1; fire the quote dialog if cost is missing
        // or stock is short. Suppressed in-helper for parts created this session.
        this.warnIfNeedsPartQuote(part, 1, newLine);
    };
    addLoadcell = (loadcell: LoadcellResponse) => this.addCalibrationItem({
        loadcellId: loadcell.loadcellId,
    });
    addAsset = (asset: ProductDto) => this.addHireItem({
        productId: asset.productId,
        cost: asset.cost,
    });


    addProductItem(existingItem: DealQuoteProductLineDto = null) {
        this.productItems?.push(this.newProductItem(existingItem));
        this.cdr.detectChanges();
    }
    addPartItem(existingItem: DealQuotePartLineDto = null) {
        this.partItems?.push(this.newPartItem(existingItem));
        this.cdr.detectChanges();
    }
    addCalibrationItem(existingItem: DealQuoteCalibrationLineDto = null) {
        this.calibrationItems?.push(this.newCalibrationItem(existingItem));
        this.cdr.detectChanges();
    }
    addHireItem(existingItem: DealQuoteHireLineDto = null) {
        this.hireItems?.push(this.newHireItem(existingItem));
        this.cdr.detectChanges();
    }
    addMarineItem(existingItem: DealQuoteMarineLineDto = null) {
        this.marineItems?.push(this.newMarineItem(existingItem));
        this.cdr.detectChanges();
    }

    newProductItem(existingItem: DealQuoteProductLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteProductLineId  : [ existingItem?.quoteProductLineId ?? null ],
            productId           : [ existingItem?.productId ?? null,    Validators.required],
            quantity            : [ existingItem?.quantity || 1,        [Validators.required, Validators.min(1)]],
            discount            : [ existingItem?.discount ?? 0],
            cost                : [ existingItem?.cost ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newPartItem(existingItem: DealQuotePartLineDto = null): FormGroup {
        return this._formBuilder.group({
            quotePartLineId     : [ existingItem?.quotePartLineId ?? null ],
            partId              : [ existingItem?.partId ?? null,       Validators.required],
            quantity            : [ existingItem?.quantity || 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
            level               : [ existingItem?.level ?? this.getNextLevel().toString(),  Validators.required],
            hidden              : [ false ],
            // Item 1 — visual parity with BOM Builder. Backend doesn't yet store sourceType
            // on DealQuotePartLine, so this value is form-only until the schema follows.
            sourceType          : [ (existingItem as any)?.sourceType ?? null ],
            // Per-line process correlation — bomItemId is null until the line lands in a real BOM,
            // so unsaved lines use a negative clientLineId to bind processes pre-save.
            bomItemId           : [ (existingItem as any)?.bomItemId ?? null ],
            clientLineId        : [ (existingItem as any)?.clientLineId ?? this.nextClientLineId() ],
        });
    }
    newCalibrationItem(existingItem: DealQuoteCalibrationLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteCalibrationLineId  : [ existingItem?.quoteCalibrationLineId ?? null ],
            description         : [ existingItem?.description ?? '',    Validators.required],
            notes               : [ existingItem?.notes ?? ''],
            loadcellId          : [ existingItem?.loadcellId ?? null,   Validators.required],
            quantity            : [ existingItem?.quantity || 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newHireItem(existingItem: DealQuoteHireLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteHireLineId     : [ existingItem?.quoteHireLineId ?? null ],
            productId           : [ existingItem?.productId ?? null,        Validators.required],
            hireFrom            : [ existingItem?.hireFrom ?? new Date(),   Validators.required],
            hireTo              : [ existingItem?.hireTo ?? null],
            cost                : [ existingItem?.cost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newMarineItem(existingItem: DealQuoteMarineLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteMarineLineId   : [ existingItem?.quoteMarineLineId ?? null ],
            partName            : [ existingItem?.partName ?? '',       Validators.required],
            supplier            : [ existingItem?.supplier ?? '',       Validators.required],
            category            : [ existingItem?.category ?? ''],
            quantity            : [ existingItem?.quantity || 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            internalCost        : [ existingItem?.internalCost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }

    deleteProductItem(index: number) {
        this.productItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deletePartItem(index: number) {
        this.partItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteCalibrationItem(index: number) {
        this.calibrationItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteHireItem(index: number) {
        this.hireItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteMarineItem(index: number) {
        this.marineItems?.removeAt(index);
        this.cdr.detectChanges();
    }

    productIsSelected = (product: DlmProductDto) =>
        this.productItems?.controls?.some(x => x.value.productId == product.dlmProductId) ||
        this.hireItems?.controls?.some(x => x.value.productId == product.dlmProductId);

    partIsSelected = (part: PartResponse) => this.partItems?.controls?.some(x => x.value.partId == part.id);
    loadcellIsSelected = (loadcell: LoadcellResponse) => this.calibrationItems?.controls?.some(x => x.value.loadcellId == loadcell.loadcellId);
    assetIsSelected = (asset: ProductDto) => this.hireItems?.controls?.some(x => x.value.productId == asset.productId);

    onPartSelected(itemControl: FormGroup, part: PartResponse) {
        itemControl.controls['cost'].setValue(part.cost ?? 0);
        const qty = itemControl?.controls?.['quantity']?.value ?? 1;
        // Fires the Record Part Quote dialog if cost is missing or stock is short.
        // Internally suppresses for parts in newlyCreatedPartIds.
        this.warnIfNeedsPartQuote(part, qty, itemControl);
        // Resolve sub-BOM + drawings the moment a part is picked so the expansion panel shows fresh data.
        this.ensureLinkedSubBomLoaded(part.id);
        this.loadPartFiles(part.id);
    }

    // Single dispatch for the part autocomplete's (optionSelected) — handles both the
    // "(Create New)..." sentinel value (0) and existing part rows. Mirrors BOM Builder's
    // createNewPart pattern so the two flows stay in sync.
    onPartAutocompleteSelected(itemControl: FormGroup, partId: number) {
        if (partId === 0) {
            this.createNewPart(itemControl);
            return;
        }
        const part = this.parts.find(p => p.id === partId);
        if (part) this.onPartSelected(itemControl, part);
    }

    // Create a new draft Part from the wizard. Mirrors BOM Builder.createNewPart but keeps
    // the dialog minimal — fields the wizard has data for. Status: Draft so it stays hidden
    // from the Parts Register until the deal is won (sweep activates it).
    createNewPart(itemControl: FormGroup) {
        // Display override: enum stores `Mm` PascalCase but the UI surfaces
        // the lowercase SI symbol "mm". Mirror of the bom-builder unitTypes
        // builder.
        const unitTypes = Object.values(PartUnitType)
            .filter(x => isNumber(x))
            .map(x => <EnumViewModel>{ name: x === PartUnitType.Mm ? 'mm' : PartUnitType[+x].toString(), value: <PartUnitType>x });

        const dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: 'New Part',
                items: [
                    { name: 'part number', propertyName: 'number', initialValue: this.newPartName, colSpan: 12, mandatory: true },
                    { name: 'description', initialValue: 'Created through Quote Wizard' },
                    { name: 'customer part no', propertyName: 'supplierPartNumber', initialValue: '', colSpan: 6 },
                    // Storage Location uses the shared tiered picker (Area > Zone >
                    // Rack > Shelf > Bin) via the reusable storage-location field.
                    // propertyName binds locationId; the afterClosed handler below
                    // copies the picked location's name into part.bin for display
                    // consistency with legacy data. disableConfig greys it out when
                    // Non Stock is on.
                    { name: 'storage location', propertyName: 'locationId', type: 'storage-location',
                        initialValue: null, colSpan: 6,
                        disableConfig: { property: 'isNonStock', value: true } },

                    { name: 'BS Audit',           propertyName: 'isBsAudit',       type: 'toggle', initialValue: false, colSpan: 3 },
                    { name: 'Atex Part',          propertyName: 'isAtexApproved',  type: 'toggle', initialValue: false, colSpan: 3 },
                    { name: 'High Risk',          propertyName: 'isHighRisk',      type: 'toggle', initialValue: false, colSpan: 3 },
                    { name: 'Critical Component', propertyName: 'isCritical',      type: 'toggle', initialValue: false, colSpan: 3 },

                    { name: 'Non Stock',           propertyName: 'isNonStock',    type: 'toggle', initialValue: false, colSpan: 3 },
                    { name: 'minimum stock level', propertyName: 'minimumStock', type: 'number', initialValue: 0,     colSpan: 3, mandatory: true },
                    { name: 'maximum stock level', propertyName: 'maximumStock', type: 'number', initialValue: 0,     colSpan: 3, mandatory: true },
                    { name: 'stock unit',          propertyName: 'partUnitType', type: 'select', options: unitTypes, initialValue: PartUnitType.EA, colSpan: 3 },

                    { name: 'cost',  propertyName: 'cost',  type: 'number', initialValue: 0, colSpan: 6, mandatory: true },
                    { name: 'price', propertyName: 'price', type: 'number', initialValue: 0, colSpan: 6 },
                ],
                overrideWidth: 60,
            }
        });

        dialog.afterClosed().subscribe(dialogRes => {
            if (!dialogRes) {
                // User cancelled — clear the form value so the autocomplete doesn't get stuck on 0.
                itemControl.controls['partId'].setValue(null);
                return;
            }

            // Hidden from registers until the deal is won; activation sweep flips Draft→Active.
            dialogRes.status = LifecycleStatus.Draft;

            // Storage Location picker stores the locationId; copy the location's
            // display name into the legacy bin field so part displays/searches
            // that key on bin still work. Mirror of bom-builder.createNewPart.
            if (dialogRes.locationId) {
                var picked = this.partLocations.find(l => l.partLocationId === dialogRes.locationId);
                if (picked) dialogRes.bin = picked.name ?? '';
            }

            this.partService.apiPartPartPost$Json$Response({ body: dialogRes }).subscribe(res => {
                if (!res?.body?.data) {
                    itemControl.controls['partId'].setValue(null);
                    return;
                }
                const newPart = res.body.data;
                this.parts.push(newPart);
                this.filteredParts = this.parts;
                // Mark as newly-created so warnIfNeedsPartQuote suppresses the quote dialog
                // for this part on every path (initial add, qty change, substitute swap).
                this.newlyCreatedPartIds.add(newPart.id);
                itemControl.controls['partId'].setValue(newPart.id);
                this.newPartName = '';
                // Same wiring as picking an existing part — seed cost + sub-BOM/drawings caches.
                this.onPartSelected(itemControl, newPart);
            });
        });
    }

    /** @deprecated kept for legacy callers; superseded by warnIfNeedsPartQuote */
    warnIfZeroStock(part: PartResponse) {
        if (!part || part.isNonStock || (part.currentStock ?? 0) > 0) return;

        const ref = this.dialog.open(RecordPartQuoteDialogComponent, {
            data: {
                partId: part.id,
                partNumber: part.number,
                currentCost: part.cost,
            },
            autoFocus: false,
        });

        ref.afterClosed().subscribe(result => {
            if (!result) return;
            // Refresh part data so cost on existing lines reflects the new value.
            this.loadParts();
        });
    }
    
    filterCompanies(event: any) {
        var text = event.target.value;
        this.filteredCompanies = this.companies.filter(m => m.name.toLowerCase().includes(text.toLowerCase()));
        this.cdr.detectChanges();
    }
    filterProducts(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageProducts(true);
    }
    filterParts(event: any) {
        const input = event.target.value ?? '';
        this.filteredParts = this.parts.filter(x =>
            x.number?.toLowerCase().includes(input.toLowerCase())
            || x.description?.toLowerCase().includes(input.toLowerCase())
        );
        // Captured for the "(Create New)..." mat-option label; cleared on successful create.
        this.newPartName = input;
    }
    filterLoadcells(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageLoadcells(true);
    }
    filterAssets(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageAssets(true);
    }
    filterMarineParts(event: any) {
        this.filteredMarineParts = this.marineParts.filter(x => x.partName.toLowerCase().includes(event.target.value.toLowerCase()));
    }
    filterMarinePartCategoriess(event: any) {
        this.filteredMarinePartCategories = this.marinePartCategories.filter(x => x.toLowerCase().includes(event.target.value.toLowerCase()));
    }

    getDiscountPercentage(itemControl: FormGroup, isHire: boolean = false) {
        var discount = itemControl.controls['discount'].value;
        var totalPrice = itemControl.controls['cost'].value * (isHire? this.getHireQuantity(itemControl) : itemControl.controls['quantity'].value);

        if(!totalPrice) return 0;

        return (100 * discount / totalPrice).toFixed(2);
    }

    updateDiscountFromPercentage(event: any, itemControl: FormGroup, isHire: boolean = false) {
        var discountPercentage = +(event.target.value) / 100;
        
        var totalPrice = itemControl.controls['cost'].value * (isHire? this.getHireQuantity(itemControl) : itemControl.controls['quantity'].value);

        itemControl.controls['discount'].setValue((totalPrice * discountPercentage).toFixed(2));

        //regular onchange autosave
        this.productsForm?.valid && this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    getOrderType = () => <number>this.getDealType();

    isCustomQuote = () => !!this.customerForm?.controls['isCustomQuote'].value;
    isRepeatCustomProduct = () => !!(this.customerForm?.controls['isRepeatCustomProduct']?.value ?? this.deal?.isRepeatCustomProduct ?? false);
    getDealType = () => <DealType>this.customerForm?.controls['dealType'].value;

    onCompanySelected(company: CustomerListItemDto) {
        this.customerForm.controls['companyId'].setValue(company.customerId);
        this.selectedCompanyName = company.name;
        this.cdr.detectChanges();
        this.regenTitle();

        // Pull this company's full detail (invoice addresses) + its contacts.
        // loadSelectedCompany defaults the invoice address / VAT in its tap, so
        // by the time this subscribe fires the summary is already populated and
        // the save persists it.
        this.loadSelectedCompany(company.customerId).subscribe(() => {
            if(this.customerForm.valid)
                this.save(true, false, this.deal?.isWizardInFirstStage);
        });
    }

    // When a company has exactly one invoice address, pre-select it and copy its
    // VAT rate so the summary populates without the user re-picking it. We only
    // auto-pick the unambiguous single-address case and never override a choice
    // already made / loaded from an existing deal.
    private applyDefaultInvoiceAddress() {
        if (!this.summaryForm) return;
        if (this.summaryForm.controls['invoiceAddressId']?.value) return;

        var addresses = this.getCompanyInvoiceAddresses();
        if (addresses.length !== 1) return;

        var address = addresses[0];
        this.summaryForm.controls['invoiceAddressId'].setValue(address.customerAddressId);
        this.summaryForm.controls['vatRate'].setValue(address.vatRate);
        this.cdr.detectChanges();
    }

    regenTitle() {
        var customerRef = this.customerForm?.controls['customerReqNo'].value || '';
        var quoteRef = this.deal?.quoteNumber || this.deal?.quoteRef || '';

        if(customerRef && quoteRef && this.selectedCompanyName) {
            var title = `${this.deal.quoteRef} ${this.selectedCompanyName} ${customerRef}`.trim();
            this.customerForm.controls['name'].setValue(title);
        }

        return true;
    }

    getCompanyContacts() {
        var companyId = this.customerForm?.controls['companyId'].value;
        return (companyId ? this.contacts?.filter(x => x.customerId == companyId) : this.contacts) ?? [];
    }

    getCompanyInvoiceAddresses() {
        return this.getCompany()?.invoiceAddresses ?? [];
    }

    formatInvoiceAddress(address: CustomerInvoiceAddressDto): string {
        if (!address) return '';
        return [address.addressOne, address.addressTwo, address.city, address.county, address.postcode, address.country]
            .filter(p => !!p?.toString().trim())
            .join(', ');
    }

    onCompanyInvoiceAddressChange(event: MatSelectChange) {
        var invoiceAddressId = event?.value;
        var invoiceAddress = this.getCompanyInvoiceAddresses()?.find(x => x.customerAddressId == invoiceAddressId);

        if(invoiceAddress)
            this.summaryForm.controls['vatRate'].setValue(invoiceAddress.vatRate);
        
        if(this.customerForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    onMarginTypeChanged(event: MatSelectChange) {
        const type = event?.value;
        // Each option seeds the % from /settings/markups: Standard/Custom use the
        // single Markup field (readonly); Charge Margin % pre-fills the summed
        // total but stays editable so the user can fine-tune. The control stays
        // ENABLED throughout so its value flows through summaryForm.value into
        // buildModel and persists.
        if (type === 2) this.summaryForm.controls['margin'].setValue(this.getChargeMarginPercent(), {emitEvent: false});
        else if (type === 3) this.summaryForm.controls['margin'].setValue(this.getStandardMarkupPercent(), {emitEvent: false});
        else if (type === 4) this.summaryForm.controls['margin'].setValue(this.getCustomMarkupPercent(), {emitEvent: false});

        this.summaryForm.controls['margin'].updateValueAndValidity({onlySelf: false});

        if(this.summaryForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    // Margin % is hand-editable only under 'Charge Margin %' (2). The Standard
    // and Custom presets are settings-driven, so the field renders readonly.
    isMarginManual = () => this.summaryForm?.controls['marginType'].value === 2;

    getCompany() {
        // Full detail (invoice addresses) lives in selectedCompany, fetched on
        // select / deal load. The slim `companies` list only carries id + name.
        var companyId = this.customerForm?.controls['companyId'].value;
        return (companyId && this.selectedCompany?.customerId == companyId) ? this.selectedCompany : null;
    }

    getContactEmail() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.email ?? '';
    }

    getContactPhoneNumber() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.phoneNumber ?? '';
    }

    getContactName() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.name;
    }

    lookupProductById(productId: number) {
        if(!productId)
            return null;

        return this.products?.find(x => x.dlmProductId == productId) ?? null;
    }
    lookupPartById(partId: number) {
        if(!partId)
            return null;

        return this.parts?.find(x => x.id == partId) ?? null;
    }
    lookupLoadcellById(loadcellId: number) {
        if(!loadcellId)
            return null;

        return this.loadcells?.find(x => x.loadcellId == loadcellId) ?? null;
    }
    lookupAssetById(assetId: number) {
        if(!assetId)
            return null;

        return this.assets?.find(x => x.productId == assetId) ?? null;
    }

    lookupProduct(itemControl: FormGroup) {
        var productId = itemControl.controls['productId'].value;
        return this.lookupProductById(productId);
    }
    lookupPart(itemControl: FormGroup) {
        var partId = itemControl.controls['partId'].value;
        return this.lookupPartById(partId);
    }
    lookupLoadcell(itemControl: FormGroup) {
        var loadcellId = itemControl.controls['loadcellId'].value;
        return this.lookupLoadcellById(loadcellId);
    }
    lookupAsset(itemControl: FormGroup) {
        var productId = itemControl.controls['productId'].value;
        return this.lookupAssetById(productId);
    }


    onCategoryToggled(event: MatOptionSelectionChange, cat: DealCategory) {
        if (!event?.isUserInput || !event.source.selected)
            return;
        
        // var cats : DealCategory[] = [cat, ...this.customerForm.controls['dealCategories'].value];

        // if(cat == DealCategory.StandardProducts) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Standard);
        // }
        // else if(cat == DealCategory.CustomProducts) {
        //     cats = cats.filter(x => x != DealCategory.StandardProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(true);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Custom);
        // }
        // else if(cat == DealCategory.RepeatCustomProduct) {
        //     cats = cats.filter(x => x != DealCategory.StandardProducts && x != DealCategory.CustomProducts);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(true);
        //     this.customerForm.controls['dealType'].setValue(DealType.RepeatCustom);
        // }
        // else if(cat == DealCategory.MarineSales) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Marine);
        // }
        // else if(cat == DealCategory.Hire) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Hire);
        // }

        switch(cat) {
            case DealCategory.StandardProducts:
                this.changeQuoteType(<any>{value: 0});
                break;
            case DealCategory.RepeatCustomProduct:
                this.changeQuoteType(<any>{value: 1});
                break;
            case DealCategory.CustomProducts:
                this.changeQuoteType(<any>{value: 2});
                break;
            //no calibration
            case DealCategory.Hire:
                this.changeQuoteType(<any>{value: 4});
                break;
            case DealCategory.MarineSales:
                this.changeQuoteType(<any>{value: 5});
                break;
        }

        if(this.customerForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    changeQuoteType(event: MatRadioChange) {
        this.customerForm.controls['dealType'].setValue(<DealType>event?.value);
        this.customerForm.controls['isCustomQuote'].setValue(event?.value == 2);
        this.customerForm.controls['isRepeatCustomProduct'].setValue(event?.value == 1);

        // Switching type switches the BASE the total is built from (raw-cost build-up
        // vs already-loaded list price), so the default margin has to follow it or the
        // quote keeps the rate the previous type earned. Only for a NEW quote still on
        // the auto default: a saved margin, or a preset someone picked deliberately, is
        // left exactly as it is.
        if (this.deal?.margin == null && this.summaryForm?.controls['marginType'].value === 2)
            this.summaryForm.controls['margin'].setValue(this.getDefaultMarginPercent(), { emitEvent: false });

        (this.productsForm.get('productLines') as FormArray).updateValueAndValidity({ onlySelf: true });
        (this.productsForm.get('partLines') as FormArray).updateValueAndValidity({ onlySelf: true });

        this.page.pageIndex = 0;

        if(this.isCustomQuote())
            this.pageParts(true);
        else if (this.getDealType() == DealType.Calibration)
            this.pageLoadcells(true);
        else if (this.getDealType() == DealType.Hire)
            this.pageAssets(true);
        else
            this.pageProducts(true);

        var cats = this.customerForm.controls['dealCategories'].value.filter((x: DealCategory) =>
            !(
                this.isCustomQuote() ? [DealCategory.StandardProducts, DealCategory.RepeatCustomProduct] :
                this.getDealType() == DealType.RepeatCustom ? [DealCategory.CustomProducts, DealCategory.StandardProducts] :
                [DealCategory.CustomProducts, DealCategory.RepeatCustomProduct]
            )
            .includes(x));

        cats.push(
            this.isCustomQuote() ? DealCategory.CustomProducts :
            this.getDealType() == DealType.RepeatCustom ? DealCategory.RepeatCustomProduct :
            this.getDealType() == DealType.Hire ? DealCategory.Hire :
            this.getDealType() == DealType.Marine ? DealCategory.MarineSales :
            DealCategory.StandardProducts
        );

        this.customerForm.controls['dealCategories'].setValue(getUniqueOnSelf(cats));
    }
    
    onMarinePartSelected(marinePart: MarinePartDto, itemControl: FormGroup) {
        itemControl.controls['partName'].setValue(marinePart.partName); //have to set this in order for the update to go through as triggers before change
        itemControl.controls['supplier'].setValue(marinePart.supplier ?? '');
        itemControl.controls['cost'].setValue(marinePart.price);
        itemControl.controls['internalCost'].setValue(marinePart.cost);

        if(this.productsForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    getHireQuantity(itemControl: FormGroup) {
        var hireFrom = itemControl.controls['hireFrom'].value;
        var hireTo = itemControl.controls['hireTo'].value;

        if(!hireTo) return 1;
        else return TimeUtil.getDaysBetween(new Date(hireFrom), new Date(hireTo));
    }

    getHireQuantityFromItem(item: DealQuoteHireLineDto) {
        var hireFrom = item.hireFrom;
        var hireTo = item.hireTo;

        if(!hireTo) return 1;
        else return TimeUtil.getDaysBetween(new Date(hireFrom), new Date(hireTo));
    }

    getTotalPrice() {
        if(this.isCustomQuote())
            return this.getGrandTotal();
        else if (this.getDealType() == DealType.Calibration)
            return sumList(this.calibrationItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
        else if (this.getDealType() == DealType.Hire)
            return sumList(this.hireItems?.controls?.map(x =>
                (x.value.hireTo ? TimeUtil.getDaysBetween(new Date(x.value.hireFrom), new Date(x.value.hireTo)) : 1) * x.value.cost - x.value.discount));
        else if (this.getDealType() == DealType.Marine)
            return sumList(this.marineItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
        else
            return sumList(this.productItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
    }

    // The two margin presets read the SINGLE 'Markup' field for the product
    // class from /settings/markups (not the summed components — that summing is
    // the BOM Builder's rule for costing custom products up from raw part cost).
    // The % is applied as a markup on the line sale price in getMarginModifier().
    getStandardMarkupPercent = () => this.markupConfig?.standardMarkup ?? 0;
    getCustomMarkupPercent = () => this.markupConfig?.customMarkup ?? 0;

    // 'Charge Margin %' = the fully-loaded charge as an EFFECTIVE percentage,
    // computed COMPOUND to match the BOM Builder's getTotalMarkup(): the four
    // overhead lines (workshop + sales + admin + uplift) load the cost first,
    // then Markup is applied on top of that loaded cost (margin-on-loaded-cost),
    // NOT a flat sum of all five. e.g. standard 28/20/24/0/15 ->
    // ((1+0.72)*(1+0.15)-1)*100 = 97.8%, not 87%. The field stays editable.
    // getMarginModifier() applies the seeded % once as base * (1 + margin/100),
    // which reproduces the compound sell price exactly.
    getChargeMarginPercent(): number {
        const c = this.markupConfig;
        if (!c) return 0;

        const custom = this.isCustomQuote();

        const overheads = (custom
            ? (c.customWorkshop ?? 0) + (c.customSales ?? 0) + (c.customAdmin ?? 0) + (c.customUplift ?? 0)
            : (c.standardWorkshop ?? 0) + (c.standardSales ?? 0) + (c.standardAdmin ?? 0) + (c.standardUplift ?? 0)) / 100;

        const markup = (custom ? (c.customMarkup ?? 0) : (c.standardMarkup ?? 0)) / 100;

        return ((1 + overheads) * (1 + markup) - 1) * 100;
    }

    /**
     * The margin a NEW quote opens on, decided by the quote's own type. Both branches
     * read /settings/markups, so DLM change the rates there and quotes follow: nothing
     * about the split is hardcoded except WHICH config each type draws from.
     *
     * The asymmetry is deliberate, because the two types arrive with DIFFERENT bases:
     *
     *  - CUSTOM: getTotalPrice() returns getGrandTotal(), a RAW-COST build-up
     *    (materials + cost-centre processes + assembly/subcontract). Nothing has loaded
     *    it, so the full custom charge must be applied HERE or the overheads are never
     *    recovered at all. With 23/16/20/55/30 that is ((1+1.14)×(1+0.30)-1)×100 =
     *    178.2%, i.e. cost ×2.782. The old default of 15% quoted these at ×1.15, below
     *    the 114% overhead alone, so every custom job went out loss-making.
     *
     *  - EVERYTHING ELSE: product lines seed from the product's LIST PRICE, which the
     *    server already built as cost × (1+overheads) × (1+markup) in
     *    SyncProductPriceFromBom. The standard rate has therefore ALREADY been applied
     *    once, so the default here is 0 and list price is the sell. Applying it again
     *    is what made standard quotes read ~15% high.
     *
     * Sales can still override per quote: the field stays editable under Charge Margin %.
     */
    getDefaultMarginPercent(): number {
        return this.isCustomQuote() ? this.getChargeMarginPercent() : 0;
    }

    getMarginModifier() {
        var marginType = this.summaryForm?.controls['marginType'].value;
        var marginValue = this.summaryForm?.controls['margin'].value / 100;

        switch(marginType) {
            case 0: return 1;               // no margin
            case 1: return marginValue;     // legacy 'sell at margin %'
            // 2 = manual Charge Margin %, 3 = Standard preset, 4 = Custom preset.
            // All apply the % as a markup on the base: base × (1 + margin/100).
            case 2:
            case 3:
            case 4: return marginValue + 1;
            default: return 1;
        }
    }


    buildModel() {
        var customerValue = this.customerForm.value as DealDto;
        var summaryValue = this.summaryForm.value as DealDto;

        var quote = Object.assign({}, this.deal, customerValue);


        quote.stageId ||= this.stages[0]?.stageId;


        quote.margin = summaryValue.margin ?? 0;
        quote.incoterm = summaryValue.incoterm;
        quote.freightTerm = summaryValue.freightTerm;
        quote.transportMode = summaryValue.transportMode;
        quote.paymentTerm = summaryValue.paymentTerm;
        quote.freightCost = summaryValue.freightCost ?? 0;
        quote.vatRate = summaryValue.vatRate;

        quote.userId = this.appAuth.getUser()?.id ?? null;
        quote.value = this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value;
        quote.comments = [];

        // line items
        var productLines = [];
        var partLines = [];
        var calibrationLines = [];
        var hireLines = [];
        var marineLines = [];

        if(this.isCustomQuote())
            this.partItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuotePartLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                partLines?.push(item);
            });
        else if (this.getDealType() == DealType.Calibration)
            this.calibrationItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteCalibrationLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                calibrationLines?.push(item);
            });
        else if (this.getDealType() == DealType.Hire)
            this.hireItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteHireLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                hireLines?.push(item);
            });
        else if (this.getDealType() == DealType.Marine)
            this.marineItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteMarineLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                marineLines?.push(item);
            });
        else
            this.productItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteProductLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                productLines?.push(item);
            });

        quote.quoteProductLines = productLines;
        quote.quotePartLines = partLines;
        quote.quoteCalibrationLines = calibrationLines;
        quote.quoteHireLines = hireLines;
        quote.quoteMarineLines = marineLines;

        quote.isWizardInProgress = true;

        return quote;
    }

    openPastOrders() {
        if(!this.selectedCompanyName) return;

        var getVatMultiplier = (vat: VatOption) => vat == VatOption.Reduced ? 1.05 : vat == VatOption.Standard ? 1.2 : 1;
        
        this.purchaseOrderService.apiPurchaseOrderGetAllGet$Json$Response({isSupplier: false, customer: this.selectedCompanyName, isDraft: false}).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data;

            this._infoDialogService.open({
                title  : 'Customer Order History',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div>
                            <div class="text-xl">${this.selectedCompanyName}</div>
                            <div class="">${purchaseOrders.length} previous orders found</div>
                        </div>
                        ${
                            purchaseOrders.map(po => `
                                <div class="border rounded-xl p-5 flex flex-col gap-2">
                                    <div class="flex justify-between">
                                        <div>
                                            <div class="text-xl">${po.salesOrderRef}</div>
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                        </div>
                                        <div>
                                            <div>Order Total</div>
                                            <div class="text-xl text-primary">${this.currencyPipe.transform(
                                                sumList(po.purchaseOrderHireItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat))) +
                                                sumList(po.purchaseOrderProductItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat))) +
                                                sumList(po.purchaseOrderPartItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat)))
                                                , 'GBP')}</div>
                                        </div>
                                    </div>
                                    <div class="w-full border-b"></div>
                                    <div class="grid grid-cols-6 gap-2 text-sm uppercase">
                                        <div class="col-span-3">Description</div>
                                        <div>QTY</div>
                                        <div>Unit Price</div>
                                        <div>Total</div>
                                    </div>
                                    <div class="w-full border-b"></div>
                                    ${
                                        po.purchaseOrderProductItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    ${
                                        po.purchaseOrderHireItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    ${
                                        po.purchaseOrderPartItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    <div class="w-full border-b"></div>
                                    <div class="grid grid-cols-6 gap-2">
                                        <div class="col-span-3">VAT</div>
                                        <div></div>
                                        <div></div>
                                        <div>${this.currencyPipe.transform(
                                            sumList(po.purchaseOrderHireItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1))) +
                                            sumList(po.purchaseOrderProductItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1))) +
                                            sumList(po.purchaseOrderPartItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1)))
                                            , 'GBP'
                                        )}</div>
                                    </div>
                                </div>
                            `).join('')
                        }
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    openLoadcellOverview(loadcellId: number) {
        this.dialog.open(CellInformationDialogComponent, {
            panelClass: "cell-information-dialog",
            data: { loadcellId: loadcellId },
        });
    }

    openProductOverview(product: DlmProductDto) {
        var request = {
            PageSize: 9999,
            PageIndex: 0,
            IsSupplier: false,
            ProductId: product.dlmProductId,
        };
        
        this.purchaseOrderService.apiPurchaseOrderPaginatedListGet$Json$Response(request).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data.items;

            this._infoDialogService.open({
                title  : 'Product Overview',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div class="grid grid-cols-2 gap-y-4 gap-x-6">
                            <div>Product Number<br/>${displayProductNumber(product.partNumber)}</div>
                            <div>Current Stock<br/>${product.freeStock} in stock</div>
                            <div class="col-span-2">Description<br/>${product.description}</div>
                        </div>

                        <div class="w-full border-b"></div>
                        <div>
                            <div class="text-xl">Sales History</div>
                            <div>${purchaseOrders.length} previous orders found</div>
                        </div>
                        
                        ${purchaseOrders.length ? `
                            <div class="border rounded-xl p-5 flex flex-col gap-2">
                                <div class="grid grid-cols-6 gap-2 text-sm uppercase">
                                    <div>Date</div>
                                    <div>Ref</div>
                                    <div class="col-span-2">Customer</div>
                                    <div>Quantity</div>
                                    <div>Total</div>
                                </div>
                                <div class="w-full border-b"></div>
                                ${
                                    purchaseOrders.map(po => `
                                        <div class="grid grid-cols-6 gap-2">
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                            <div>${po.salesOrderRef}</div>
                                            <div class="col-span-2">${po.company}</div>
                                            <div>${po.itemCount}</div>
                                            <div>${this.currencyPipe.transform(po.amount, 'GBP')}</div>
                                        </div>
                                    `).join('')
                                }
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>Total Sales<br/>${sumList(purchaseOrders.map(x => x.itemCount))}</div>
                                <div>Total Revenue<br/>${this.currencyPipe.transform(sumList(purchaseOrders.map(x => x.amount)), 'GBP')}</div>
                            </div>
                        ` : ``}
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    openAssetOverview(asset: ProductDto) {
        var request = {
            PageSize: 9999,
            PageIndex: 0,
            IsSupplier: false,
            ProductName: asset.name
        };
        
        this.purchaseOrderService.apiPurchaseOrderPaginatedListGet$Json$Response(request).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data.items;

            this._infoDialogService.open({
                title  : 'Asset Overview',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div class="grid grid-cols-2 gap-y-4 gap-x-6">
                            <div>Asset Name<br/>${asset.name}</div>
                            <div>Current Stock<br/>${asset.stock} in stock</div>
                            <div class="col-span-2 whitespace-pre">Description<br/>${asset.description}</div>
                        </div>

                        <div class="w-full border-b"></div>
                        <div>
                            <div class="text-xl">Sales History</div>
                            <div>${purchaseOrders.length} previous orders found</div>
                        </div>
                        
                        ${purchaseOrders.length ? `
                            <div class="border rounded-xl p-5 flex flex-col gap-2">
                                <div class="grid grid-cols-5 gap-2 text-sm uppercase">
                                    <div>Date</div>
                                    <div>Ref</div>
                                    <div class="col-span-2">Customer</div>
                                    <div>Total</div>
                                </div>
                                <div class="w-full border-b"></div>
                                ${
                                    purchaseOrders.map(po => `
                                        <div class="grid grid-cols-5 gap-2">
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                            <div>${po.salesOrderRef}</div>
                                            <div class="col-span-2">${po.company}</div>
                                            <div>${this.currencyPipe.transform(po.amount, 'GBP')}</div>
                                        </div>
                                    `).join('')
                                }
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>Total Hires<br/>${purchaseOrders.length}</div>
                                <div>Total Revenue<br/>${this.currencyPipe.transform(sumList(purchaseOrders.map(x => x.amount)), 'GBP')}</div>
                            </div>
                        ` : ``}
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    sendToSales() {
        var salesTeam = this.allUsers.filter(x => x.teamRole == +TeamRole.Sales).map(x => <EnumViewModel> {value: x.id, name: `${x.firstName} ${x.lastName}`});

        //open sales dialog to set user
        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Assign to Sales Person",
                items: [
                    {name: 'sales person', propertyName: 'userId', type: 'select', options: salesTeam, initialValue: null, mandatory: true},
                ],
                additionalHtml: `
                    <div class="p-5 bg-[#f0f9ff] border border-[#bfdbfe] rounded-lg">
                        <div class="text-[#1e40af]">
                            <strong>Note:</strong> The assigned sales person will be responsible for reviewing the custom product requirements, finalizing pricing, and managing the customer relationship.
                        </div>
                    </div>
                `,
                overrideSaveText: 'Send to Sales',
            }
        });

        dialog.afterClosed().subscribe(async (dialogRes) => {
            if (!dialogRes) return;

            var form = this.buildModel();
            form.isWizardInProgress = true;
            form.userId = dialogRes.userId;
            form.isSentToSales = true;

            this.setStageToNegotiation(form);

            form = await this.saveDealApiAsync(form);

            //send notification (and email) to sales person
            this.dealService.apiDealSendSalesNotificationPut$Json$Response({body: form, userId: form.userId}).subscribe();

            //save BOM to "Custom Product"
            this.customProductName ||= 'Custom Product for ' + form.quoteRef;
            // var existingProduct = this.products.find(x => x.partNumber.toLowerCase() == this.customProductName.toLowerCase());
            var existingProduct = this.products.find(x => x.dlmProductId == this.bom?.productId);

            if(this.deal?.bomId && this.bom?.productId && existingProduct) {
                //Update existing BOM
                this.saveBom(form, existingProduct);
            }
            else { 
                //Create new custom product for BOM. Starts as Draft; the
                // DealRepository activation sweep flips it to Active when the deal moves
                // to a "won" stage so it appears in the Product Register.
                this.dlmProductService.apiDlmProductAddProductPost$Json$Response({
                    setGeneratedPartNumber: true,
                    body: {
                        partNumber: this.customProductPartNumber,
                        description: this.customProductName,
                        cost: this.getGrandTotal(),
                        price: this.getGrandTotal(), //add margins like in regular bom?
                        isCustomProduct: true,
                        status: LifecycleStatus.Draft,
                }}).subscribe(res => {
                    if(!res?.body?.data) return;

                    this.customProductPartNumber = res.body.data.partNumber;

                    //save BOM to new product
                    this.saveBom(form, res.body.data);
                });
            }
        });
    }

    // onSuccess: optional callback invoked after persistence succeeds. When
    // provided, the dialog is NOT closed automatically — the caller takes over
    // (e.g. saveBomProgress wants to keep the dialog open and show a toast).
    // When omitted, the original behaviour applies: dialog closes after save.
    saveBom(quote: DealDto, product: DlmProductDto, onSuccess?: () => void) {
        this.buildBomModel(quote, product.dlmProductId);

        if(product.isCustomProduct)
            this.bom.isCustomProduct = true;

        // The API refuses a BOM with two lines on the same level (among other things) and says why
        // in the message; without this the save just did nothing.
        const refused = (message?: string | null) =>
            this._snackBar.open(message || 'The BOM could not be saved.', 'OK', { duration: 8000 });

        if(this.deal?.bomId)
            this.bomService.apiBomUpdateBomPut$Json$Response({body: this.bom}).subscribe(res => {
                if(!res?.body?.data) { refused(res?.body?.message); return; }
                this.bom = res.body.data;
                // Echo persisted BomItemIds back onto the form so a later save
                // updates these rows instead of inserting duplicates.
                this.echoBomItemIds(this.bom);
                if (onSuccess) onSuccess();
                else this.close();
            });
        else
            this.bomService.apiBomAddBomPost$Json$Response({body: this.bom}).subscribe(res => {
                if(!res?.body?.data) { refused(res?.body?.message); return; }
                quote.bomId = res.body.data.bomId;
                this.bom = res.body.data;
                this.echoBomItemIds(this.bom);
                // Persist new bomId on deal. Close only if no onSuccess provided
                // (otherwise caller is keeping the dialog open).
                this.saveDealApi(quote, !onSuccess);
                if (onSuccess) onSuccess();
            });
    }

    /** True when the deal's current stage is the Negotiation stage. */
    isDealInNegotiation(): boolean {
        const stage = this.stages?.find(s => s.stageId === this.deal?.stageId);
        return this.dealHelper.isNegotiationStage(stage);
    }

    // Edit-BOM-mode action (custom quote in Negotiation). Builds the amended BOM +
    // deal from the form and routes straight to the revision prompt. Crucially it
    // does NOT call saveDealApiAsync / saveBom's in-place update, so the ORIGINAL
    // quote is never written.
    // Load the revision family for the open quote so the header menu can list them.
    private loadRevisions() {
        if (!this.dealId) return;
        this.dealService.apiDealRevisionsGet$Json({ dealId: this.dealId }).subscribe(res => {
            this.revisions = res?.data ?? [];
            this.cdr.detectChanges();
        });
    }

    // Open a different revision of this quote in the wizard. The revisions list
    // only carries summaries, so fetch the full deal then re-open the wizard for
    // it (matching how the board opens a quote: { deal }) and close the current
    // one. Opening a superseded revision shows its saved lines/BOM; amending one
    // would itself create a new revision via the normal flow.
    openRevision(rev: DealRevisionSummaryDto) {
        if (!rev?.dealId || rev.dealId === this.dealId) return;

        this.loader.open();
        this.dealService.apiDealDealGet$Json({ dealId: rev.dealId }).subscribe(res => {
            this.loader.close();
            var deal = res?.data;
            if (!deal) return;

            this.dialog.open(QuoteWizardDialogComponent, { disableClose: true, data: { deal } });
            this.close();
        });
    }

    saveBomRevision(): void {
        if (!this.isCustomQuote() || !this.customerForm?.valid) return;

        const existingProduct = this.products.find(x => x.dlmProductId == this.bom?.productId);
        if (!this.deal?.bomId || !existingProduct) {
            this._snackBar.open('Cannot create a revision: this quote has no saved BOM yet', 'Dismiss', { duration: 5000, panelClass: ['bg-red-600', 'text-white'] });
            return;
        }

        // Amended deal (with the edited quote lines) + amended BOM, straight from the form.
        const amendedDeal = this.buildModel();
        this.buildBomModel(amendedDeal, existingProduct.dlmProductId);

        // Skip a pointless revision when the BOM is unchanged. Biased to allow: only
        // skips when the content is provably identical to what was loaded.
        const currentSig = this.bomContentSignature(this.bom.items, this.bomAssemblyProcesses);
        if (this.loadedBomContentSig !== null && currentSig === this.loadedBomContentSig) {
            this._snackBar.open('No BOM changes to save as a revision. Use "Send Quote" to send it as-is.', 'Dismiss', {
                duration: 5000, panelClass: ['bg-gray-700', 'text-white'],
            });
            return;
        }

        this.promptSaveAsRevision(amendedDeal);
    }

    // Canonical, order-stable signature of the editable BOM content (items +
    // assembly processes). Numeric fields are coerced and both cost-centre spellings
    // are read so a load snapshot and a save-time rebuild compare equal when nothing
    // actually changed. Header fields are deliberately excluded (buildBomModel
    // rewrites them on every save, which would create false "changed" results).
    private bomContentSignature(items: any[] | undefined | null, processes: any[] | undefined | null): string {
        const num = (v: any) => (v === null || v === undefined || v === '') ? null : Number(v);
        const str = (v: any) => v ?? '';

        const itemSig = (items ?? [])
            .map(i => ({ p: num(i.partId), q: num(i.quantity), l: str(i.level), s: num(i.sourceType) }))
            .sort((a, b) => ((a.p ?? 0) - (b.p ?? 0)) || (a.l > b.l ? 1 : a.l < b.l ? -1 : 0));
        const procSig = (processes ?? [])
            .map(p => ({
                seq: num(p.sequence), op: str(p.operationCode), nm: str(p.operationName),
                cc: str(p.costCentreCode ?? p.costCenterCode), set: num(p.setupHours), run: num(p.runPerUnitHours),
                sub: !!p.isSubcontracted, subN: str(p.subcontractorName),
                link: p.bomItemId ?? p.clientLineId ?? null,
            }))
            .sort((a, b) => ((a.seq ?? 0) - (b.seq ?? 0)) || (a.op > b.op ? 1 : a.op < b.op ? -1 : 0));

        return JSON.stringify({ items: itemSig, procs: procSig });
    }

    // Confirm, then persist the amended BOM as a brand-new revision and clone the
    // quote onto it (Q1234 -> Q1234a). Cancel aborts entirely. The original quote +
    // BOM are never touched.
    private promptSaveAsRevision(amendedDeal: DealDto): void {
        const ref = this._fuseConfirmationService.open({
            title: 'Amendments detected',
            message: 'Amendments have been made to the existing quote. Save as a new revision, or cancel.',
            icon: { show: true, name: 'heroicons_outline:document-duplicate', color: 'primary' },
            actions: {
                confirm: { show: true, label: 'Save as new revision', color: 'primary' },
                cancel: { show: true, label: 'Cancel' },
            },
            dismissible: true,
        });

        ref.afterClosed().subscribe(result => {
            if (result !== 'confirmed') return; // Cancel / dismiss => abort the save entirely
            this.saveBomAsNewRevision(amendedDeal);
        });
    }

    private saveBomAsNewRevision(amendedDeal: DealDto): void {
        // this.bom already holds the amended model (buildBomModel ran in saveBomRevision).
        // 1) Save the amended BOM as a brand-new BOM (original BOM untouched).
        const clone = this.cloneBomPayloadForRevision(this.bom);

        this.loader.open();
        this.bomService.apiBomAddBomPost$Json$Response({ body: clone }).subscribe(res => {
            const newBom = res?.body?.data;
            if (!newBom?.bomId) {
                this.loader.close();
                this._snackBar.open('Failed to save amended BOM', 'Dismiss', { duration: 5000, panelClass: ['bg-red-600', 'text-white'] });
                return;
            }

            // 2) Clone the quote onto the new BOM. The backend copies the ORIGINAL's
            //    lines + scalar fields, sets the Q1234a ref, and flips the predecessor's
            //    IsLatestRevision. The original deal itself is never mutated.
            this.dealService.apiDealCloneDealRevisionPost$Json({ sourceDealId: this.deal.dealId, newBomId: newBom.bomId }).subscribe(resp => {
                const newDeal = resp?.data;
                if (!newDeal?.dealId) {
                    this.loader.close();
                    this._snackBar.open('Failed to create quote revision', 'Dismiss', { duration: 5000, panelClass: ['bg-red-600', 'text-white'] });
                    return;
                }

                // 3) Overwrite the NEW revision's quote lines with the amended ones, so
                //    Q1234a's lines match its amended BOM. Targets the new deal only —
                //    line ids are nulled so they insert fresh (and the cloned copies are
                //    archived by UpdateQuoteDeal), never touching the original's lines.
                const revisionDeal = this.retargetDealLinesToRevision(amendedDeal, newDeal.dealId, newBom.bomId);
                this.dealService.apiDealUpdateQuoteDealPut$Json$Response({ body: revisionDeal }).subscribe(() => {
                    this.loader.close();
                    this._snackBar.open(`Saved as revision ${newDeal.quoteRef}`, 'Dismiss', {
                        duration: 4000, panelClass: ['bg-green-600', 'text-white'],
                    });
                    // Close; the board reloads and shows the new latest revision. Keeping
                    // the dialog open would still reference the old deal and could spawn
                    // further revisions off the original.
                    this.close();
                }, () => {
                    this.loader.close();
                    this._snackBar.open('Revision created but failed to save amended lines', 'Dismiss', { duration: 6000, panelClass: ['bg-red-600', 'text-white'] });
                });
            }, () => {
                this.loader.close();
                this._snackBar.open('Failed to create quote revision', 'Dismiss', { duration: 5000, panelClass: ['bg-red-600', 'text-white'] });
            });
        });
    }

    // Retarget the amended deal payload onto the new revision deal: point every line
    // at the new deal and null its line id so UpdateQuoteDeal inserts it fresh (and
    // archives the lines the clone copied). dealId/bomId are set to the new revision.
    private retargetDealLinesToRevision(amendedDeal: DealDto, newDealId: number, newBomId: number): DealDto {
        const reId = (arr: any[] | undefined | null, idKey: string) =>
            (arr ?? []).map(l => ({ ...l, [idKey]: null, dealId: newDealId }));

        return {
            ...amendedDeal,
            dealId: newDealId,
            bomId: newBomId,
            // The revision is a settled Negotiation-stage quote, NOT an in-progress
            // wizard draft. buildModel() force-sets isWizardInProgress=true; clear it
            // (and the first-stage flag) so the new card isn't yellow and reopens in
            // edit-BOM mode rather than the full send-quote wizard.
            isWizardInProgress: false,
            isWizardInFirstStage: false,
            quoteProductLines: reId(amendedDeal.quoteProductLines, 'quoteProductLineId'),
            quotePartLines: reId(amendedDeal.quotePartLines, 'quotePartLineId'),
            quoteCalibrationLines: reId(amendedDeal.quoteCalibrationLines, 'quoteCalibrationLineId'),
            quoteHireLines: reId(amendedDeal.quoteHireLines, 'quoteHireLineId'),
            quoteMarineLines: reId(amendedDeal.quoteMarineLines, 'quoteMarineLineId'),
        };
    }

    // Strip persisted ids so AddBom inserts a fresh BOM, and re-key per-line process
    // links via clientLineId so the backend (AddBom) re-resolves them to the new item
    // ids — mirrors how brand-new BOMs are saved. Assembly-level processes (no item
    // link) stay assembly-level.
    private cloneBomPayloadForRevision(bom: any): any {
        const clone: any = {
            ...bom,
            bomId: 0,
            revision: (bom.revision ?? 0) + 1,
            createdDate: new Date().toISOString(),
        };

        let nextClientId = -1;
        const linkToClientId = new Map<string, number>();
        clone.items = (bom.items ?? []).map((i: any) => {
            const cid = nextClientId--;
            if (i.bomItemId != null) linkToClientId.set(`b:${i.bomItemId}`, cid);
            if (i.clientLineId != null) linkToClientId.set(`c:${i.clientLineId}`, cid);
            return { ...i, bomItemId: null, bomId: 0, clientLineId: cid };
        });
        // engineers/contractors retired 2026-07-16 (BOM rebuild decision B9) — processes ARE the labour.
        clone.assemblyProcesses = (bom.assemblyProcesses ?? []).map((p: any) => {
            const key = p.bomItemId != null ? `b:${p.bomItemId}` : (p.clientLineId != null ? `c:${p.clientLineId}` : null);
            const cid = key ? (linkToClientId.get(key) ?? null) : null;
            return { ...p, bomAssemblyProcessId: null, bomId: 0, bomItemId: null, clientLineId: cid };
        });

        return clone;
    }

    // Step 2 "Save Progress" — lets a technician park a half-built custom BOM
    // and come back to it later (engineers often juggle multiple BOMs).
    //
    // Persists everything that's been entered so the next session restores
    // cleanly when the orange card is reopened:
    //   - Deal + line items (via saveDealApiAsync → buildModel)
    //   - Custom DlmProduct (creates a Draft one if none exists yet; mirrors
    //     sendToSales — DealRepository's activation sweep flips it to Active
    //     when the deal eventually moves to a Won stage)
    //   - BOM itself, INCLUDING assembly processes (via saveBom → buildBomModel)
    //
    // Differs from sendToSales by deliberately skipping the sale-handoff
    // side effects:
    //   - Does NOT set isSentToSales
    //   - Does NOT move the deal to the Negotiation stage
    //   - Does NOT fire apiDealSendSalesNotification
    //   - Preserves isWizardInFirstStage so the card stays orange (still on
    //     the technician's queue; the BOM isn't done yet)
    //
    // Keeps the dialog open and shows a toast on success so the technician
    // gets confirmation without losing their place in the BOM builder.
    async saveBomProgress() {
        if (!this.isCustomQuote() || !this.customerForm?.valid) return;

        // Ensure we have a product name to attach to (mirrors sendToSales).
        this.customProductName ||= 'Custom Product for ' + this.deal?.quoteRef;

        var form = this.buildModel();
        form.isWizardInProgress = true;
        // NB: isWizardInFirstStage is whatever buildModel pulled from the
        // deal — we deliberately don't change it.

        form = await this.saveDealApiAsync(form);
        if (!form) return;

        var notifySaved = () => this._snackBar.open('Progress saved', 'Dismiss', {
            duration: 3000,
            panelClass: ['bg-green-600', 'text-white'],
        });

        var existingProduct = this.products.find(x => x.dlmProductId == this.bom?.productId);

        if (this.deal?.bomId && this.bom?.productId && existingProduct) {
            // BOM + product already exist — update in place, then toast.
            this.saveBom(form, existingProduct, notifySaved);
        } else {
            // First save: create the Draft custom product, then save BOM against it.
            this.dlmProductService.apiDlmProductAddProductPost$Json$Response({
                setGeneratedPartNumber: true,
                body: {
                    partNumber: this.customProductPartNumber,
                    description: this.customProductName,
                    cost: this.getGrandTotal(),
                    price: this.getGrandTotal(),
                    isCustomProduct: true,
                    status: LifecycleStatus.Draft,
                }
            }).subscribe(res => {
                if (!res?.body?.data) return;
                this.customProductPartNumber = res.body.data.partNumber;
                // Cache the new product locally so a subsequent Save Progress
                // click in the same session finds it via the existingProduct
                // lookup above (instead of falling through to create a duplicate).
                this.products.push(res.body.data);
                this.saveBom(form, res.body.data, notifySaved);
            });
        }
    }

    // Step 1 -> Technician handoff. Persists the deal with
    // isWizardInFirstStage = true, which drives the orange card colour on the
    // pipeline board (pipeline-board.component.html keys on this flag).
    //
    // ─── STUB ────────────────────────────────────────────────────────────
    // No email / notification is sent to the technician team today. The
    // orange card is the only signal — technicians watch the pipeline board
    // for it. When wiring up actual notifications:
    //   - Backend pattern to mirror: apiDealSendSalesNotification (used by
    //     sendToSales() in this same component, line 1832). It sends both
    //     an in-app notification and an email to a specific userId.
    //   - The role to target now exists: TeamRole.Technician (= 5) was
    //     added 2026-05-11. Filter allUsers by that role to either pick a
    //     specific assignee (mirroring the sendToSales user-picker dialog)
    //     or broadcast to everyone with the role.
    // ─────────────────────────────────────────────────────────────────────
    submitToTechnician() {
        if (!this.customerForm?.valid) return;
        this.save(true, true, true);
        // TODO: send email / notification to Technician team — see STUB note above.
    }

    save(inProgress: boolean = true, close: boolean = true, isFirstStage: boolean = false) {
        var form = this.buildModel();

        form.isWizardInProgress = inProgress;
        form.isWizardInFirstStage = isFirstStage;

        if(!inProgress) {
            //send quote email to customer
            this.openQuoteEmail(form,
                () => {
                    this.setStageToNegotiation(form);
                    this.setPartsDescription(form);
                    this.saveDealApi(form, close);
                    this.createDealSalesOrder(form);
                },
                () => {
                    form.isWizardInProgress = true;
                });
        }
        else
            this.saveDealApi(form, close);
    }

    setStageToNegotiation(form: DealDto) {
        // Shared predicate = substring match; the old exact-equality check here silently
        // stopped moving finalised quotes if the stage was renamed (e.g. "In Negotiation").
        var negotiationStageId = this.stages.find(x => this.dealHelper.isNegotiationStage(x))?.stageId;
        if(negotiationStageId)
            form.stageId = negotiationStageId;
    }

    setPartsDescription(form: DealDto) {
        var dealParts = 'Items\n';
        var lineNumber = 1;

        if(form.quotePartLines.length) {
            var partLine = form.quotePartLines[0];
            dealParts += `Line ${lineNumber++}: Product ID ${this.customProductPartNumber}`;
            dealParts += `\n${this.customProductName}\n`;
            dealParts += [
                `Quantity: 1`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`,
                `Discount: £0.00`,
                `Subtotal: ${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`,
                `Lead Time: ${partLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var productLine of form.quoteProductLines) {
            dealParts += `Line ${lineNumber++}: Product ID ${this.lookupProductById(productLine.productId)?.partNumber}`;
            dealParts += `\n${this.lookupProductById(productLine.productId)?.description}\n`;
            
            dealParts += [
                `Quantity: ${productLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(productLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(productLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(productLine.cost * productLine.quantity - productLine.discount, 'GBP')}`,
                `Lead Time: ${productLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var hireLine of form.quoteHireLines) {
            dealParts += `Line ${lineNumber++}: Asset ID ${this.lookupAssetById(hireLine.productId)?.serialNumber}`;
            dealParts += `\n${this.lookupAssetById(hireLine.productId)?.name}\n`;
        
            dealParts += [
                `Quantity: ${this.getHireQuantityFromItem(hireLine)}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(hireLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(hireLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(hireLine.cost * this.getHireQuantityFromItem(hireLine) - hireLine.discount, 'GBP')}`,
                `Lead Time: ${hireLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var calibLine of form.quoteCalibrationLines) {
            dealParts += `Line ${lineNumber++}: Loadcell ID ${this.lookupLoadcellById(calibLine.loadcellId)?.bookingRef}`;
            dealParts += `\n${calibLine.description}\n`;
        
            dealParts += [
                `Quantity: ${calibLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(calibLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(calibLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(calibLine.cost * calibLine.quantity - calibLine.discount, 'GBP')}`,
                `Lead Time: ${calibLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var marineLine of form.quoteMarineLines) {
            dealParts += `Line ${lineNumber++}: Part ${marineLine.partName}`;
            dealParts += `\n${marineLine.supplier}\n`;
            
            dealParts += [
                `Quantity: ${marineLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(marineLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(marineLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(marineLine.cost * marineLine.quantity - marineLine.discount, 'GBP')}`,
                `Lead Time: ${marineLine.leadTime}\n\n`,
            ].join(' | ');
        }

        //freight, vat, totals
        dealParts += `Totals\n`;
        dealParts += [
            `Subtotal: ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier(), 'GBP')}`,
            `Freight: ${this.currencyPipe.transform(this.summaryForm.controls['freightCost'].value, 'GBP')}`,
            `VAT (${this.summaryForm.controls['vatRate'].value * 100}%): ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * this.summaryForm.controls['vatRate'].value, 'GBP')}`,
            `Total (inc VAT): ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value, 'GBP')}`,
        ].join(' | ');

        form.partsDescription = dealParts.trim();
    }

    getTodaysDate = () => new Date();

    openQuoteEmail(deal: DealDto, successCallback: () => void, failureCallback: () => void) {
        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Send Email to Customer",
                items: [
                    {name: 'to', initialValue: this.getContactEmail(), mandatory: true},
                    {name: 'subject', initialValue: `Quote ${deal.quoteNumber || deal.quoteRef || ''} - ${this.appInfo.companyName}`, mandatory: true},
                    {name: 'body', type: 'textarea', initialValue:
                      `Dear ${this.getContactName()},\n\n` +
                      "I hope you are doing well.\n\n" +
                      `Please see attached your quote for ${this.selectedCompanyName} with reference ${deal.quoteNumber || deal.quoteRef || ''}.\n\n` +
                      "Thank you again for your order. Let us know if you have any questions or require further clarification.\n\n" +
                      "Best regards,\n" +
                      this.appInfo.companyName, mandatory: true},
                ],
                overrideSaveText: 'Send',
                overrideWidth: 65,
            }
        });

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) {
                failureCallback();
                return;
            }

            //get pdf (...)
            this.preparePdf(PDF => {
                //download the pdf locally (?)
                // PDF.save(`${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`);
                var blob = PDF.output('blob');

                //send on api
                this.dealService.apiDealSendCustomerQuoteEmailPost$Json$Response({emailTo: dialogRes.to, emailSubject: dialogRes.subject, emailBody: dialogRes.body,
                    fileName: `${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`,
                    dealId: this.dealId ?? undefined,
                    body: {
                        formFile: blob
                    }
                }).subscribe(res => {
                    if(!res?.body?.data) {
                        //snack error todo
                        failureCallback();
                        return;
                    }

                    this.dealService.apiDealUploadFileDealIdPost$Json$Response({
                        dealId: this.dealId,
                        isBom: false,
                        filename: `${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`,
                        body: { formFile: blob },
                    }).subscribe(res => {
                        if(!res?.body?.data) {
                            failureCallback();
                            return;
                        }
                        successCallback();
                    });
                });
            });
        });
    }

    // After a save, the persisted ids live on the returned deal/bom, but the
    // in-memory partItems form controls still hold their pre-save ids (null for
    // newly-added lines). Without echoing them back, the NEXT save in the same
    // session re-posts each line as new and the backend's "find-by-id-else-add"
    // inserts a duplicate - this was the custom-quote BOM duplication bug
    // (1 added part -> 2 lines after the technician->sales round-trip). Match by
    // level, which is unique per quote/BOM line.
    private echoPartLineIds(deal: DealDto) {
        var lines = deal?.quotePartLines ?? [];
        this.partItems?.controls?.forEach((c: FormGroup) => {
            var level = c.get('level')?.value;
            var match = lines.find(l => l.level == level);
            if (match?.quotePartLineId != null)
                c.get('quotePartLineId')?.setValue(match.quotePartLineId, { emitEvent: false });
        });
    }

    private echoBomItemIds(bom: any) {
        var items = (bom?.items ?? []) as any[];
        this.partItems?.controls?.forEach((c: FormGroup) => {
            var level = c.get('level')?.value;
            var match = items.find(i => i.level == level);
            if (match?.bomItemId != null)
                c.get('bomItemId')?.setValue(match.bomItemId, { emitEvent: false });
        });
    }

    async saveDealApiAsync(deal: DealDto) {
        if(this.dealId) {
            var res = await this.dealService.apiDealUpdateQuoteDealPut$Json$Response({body: deal}).toPromise();
            if(!res?.body?.data) return;

            this.deal = res.body.data;
            this.dealId = this.deal.dealId;
            deal.dealId = this.dealId;
        }
        else {
            var res2 = await this.dealService.apiDealAddDealPost$Json$Response({body: deal}).toPromise();
            if(!res2?.body?.data) return;

            this.deal = res2.body.data;
            this.dealId = this.deal.dealId;
            deal.dealId = this.dealId;

            if(this.files.length) {
                var bomFiles = this.files.filter((_, i) => this.fileInfos[i].isBom);
                var nonBomFiles = this.files.filter((_, i) => !this.fileInfos[i].isBom);
                this.uploadImages(<any>bomFiles, true);
                this.uploadImages(<any>nonBomFiles, false);
            }
        }

        // Echo persisted QuotePartLineIds back onto the form so a later save
        // updates these lines instead of inserting duplicates.
        this.echoPartLineIds(this.deal);

        return this.deal;
    }

    saveDealApi(deal: DealDto, close: boolean = true) {
        if(this.dealId)
            this.dealService.apiDealUpdateQuoteDealPut$Json$Response({body: deal}).subscribe(res => {
                if(!res?.body?.data) return;

                this.deal = res.body.data;
                this.dealId = this.deal.dealId;
                deal.dealId = this.dealId;
                this.echoPartLineIds(this.deal);

                if(close)
                    this.close();
            });
        else
            this.dealService.apiDealAddDealPost$Json$Response({body: deal}).subscribe(res => {
                if(!res?.body?.data) return;

                this.deal = res.body.data;
                this.dealId = this.deal.dealId;
                deal.dealId = this.dealId;
                this.echoPartLineIds(this.deal);
                // this.buildForm();

                if(this.files.length) {
                    var bomFiles = this.files.filter((_, i) => this.fileInfos[i].isBom);
                    var nonBomFiles = this.files.filter((_, i) => !this.fileInfos[i].isBom);
                    this.uploadImages(<any>bomFiles, true);
                    this.uploadImages(<any>nonBomFiles, false);
                }

                if(close)
                    this.close();
            });
    }

    createDealSalesOrder(deal: DealDto) {
        var salesOrder = this.setupSalesOrderFromDeal(deal);
        this.purchaseOrderService.apiPurchaseOrderAddPurchaseOrderPost$Json$Response({body: salesOrder}).subscribe(res => {
            if (!res?.body?.data?.purchaseOrderId) return;

            var poId = res.body.data.purchaseOrderId;

            // Auto-generate pick sheet for the new SO
            this.pickSheetService.apiPickSheetGeneratePost$Json$Response({
                purchaseOrderId: poId
            }).subscribe();
        });
    }
    
    setupSalesOrderFromDeal(deal: DealDto) {
        var purchaseOrderProductItems: PurchaseOrderProductItemDto[] = [];
        var purchaseOrderPartItems: PurchaseOrderPartItemDto[] = [];
        var purchaseOrderHireItems: PurchaseOrderHireItemDto[] = [];

        const vat = deal.vatRate == 0.2 ? VatOption.Standard : deal.vatRate == 0.05 ? VatOption.Reduced : VatOption.Zero;
        const margin = this.getMarginModifier();

        // Per-unit SELL price the customer was quoted: the line's internal cost raised
        // by the quote's margin modifier, with any per-line discount spread across the
        // units so the SO line total matches the quoted line total. Mirrors how
        // quote.value is derived in buildModel(). BudgetedCost (set separately per line)
        // carries the raw per-unit cost as an internal reference.
        const unitSell = (unitCost: number, qty: number, discount: number = 0) =>
            ((unitCost * (qty || 1) - (discount || 0)) / (qty || 1)) * margin;

        if(this.isCustomQuote() && this.bom?.productId) {
            // Custom product: the BOM's part lines ARE the product's bill of materials,
            // not separate SO lines - so the SO carries a single rolled-up product line.
            // Sell = grand total x margin; budget = the raw BOM cost total.
            const total = this.getGrandTotal();
            purchaseOrderProductItems = [
                <PurchaseOrderProductItemDto>{
                    description: this.lookupProductById(this.bom.productId)?.partNumber,
                    productId: this.bom.productId,
                    quantity: 1,
                    price: total * margin,
                    budgetedCost: total,
                    vat,
                }
            ];
        }
        else if(this.getDealType() == DealType.Calibration) {
            // No calibration-specific PO line type exists, so each calibration is carried
            // as a product line with the calibration description (productId left 0 - the
            // PO line has no Product FK, so this is safe and just means no product lookup).
            purchaseOrderProductItems = (deal.quoteCalibrationLines ?? []).map(x =>
                <PurchaseOrderProductItemDto>{
                    description: x.description,
                    productId: 0,
                    quantity: x.quantity,
                    price: unitSell(x.cost, x.quantity, x.discount),
                    budgetedCost: x.cost,
                    vat,
                }
            );
        }
        else if(this.getDealType() == DealType.Hire) {
            purchaseOrderHireItems = (deal.quoteHireLines ?? []).map(x => {
                const days = this.getHireQuantityFromItem(x);
                return <PurchaseOrderHireItemDto>{
                    description: this.lookupProductById(x.productId)?.partNumber,
                    quantity: days,
                    hireFrom: x.hireFrom,
                    hireTo: x.hireTo,
                    price: unitSell(x.cost, days, x.discount),
                    budgetedCost: x.cost,
                    vat,
                };
            });
        }
        else if(this.getDealType() == DealType.Marine) {
            // Marine lines already store the SELL price in `cost` and the real cost in
            // `internalCost`, so margin is NOT re-applied here. No marine PO line type
            // exists, so each is carried as a part line with the part name (partId 0).
            purchaseOrderPartItems = (deal.quoteMarineLines ?? []).map(x =>
                <PurchaseOrderPartItemDto>{
                    description: x.partName,
                    partId: 0,
                    quantity: x.quantity,
                    price: (x.cost * (x.quantity || 1) - (x.discount || 0)) / (x.quantity || 1),
                    budgetedCost: x.internalCost,
                    vat,
                }
            );
        }
        else {
            // Standard products
            purchaseOrderProductItems = (deal.quoteProductLines ?? []).map(x =>
                <PurchaseOrderProductItemDto>{
                    description: this.lookupProductById(x.productId)?.partNumber,
                    productId: x.productId,
                    quantity: x.quantity,
                    price: unitSell(x.cost, x.quantity, x.discount),
                    budgetedCost: x.cost,
                    vat,
                }
            );

            // Standard quotes can also carry loose parts; these were previously dropped
            // entirely (purchaseOrderPartItems hardcoded []), so their value never
            // reached the SO. Carry them as part lines.
            purchaseOrderPartItems = (deal.quotePartLines ?? []).map(x =>
                <PurchaseOrderPartItemDto>{
                    description: this.lookupPartById(x.partId)?.number,
                    partId: x.partId,
                    quantity: x.quantity,
                    price: unitSell(x.cost, x.quantity, 0),
                    budgetedCost: x.cost,
                    vat,
                }
            );
        }

        var purchaseOrder: PurchaseOrderDto = {
            contactId: deal.contactId,
            company: this.getCompany()?.name,
            isSupplier: false,
            isProduct: true,
            title: 'Sales Order for ' + (deal.quoteNumber || deal.quoteRef || ''),
            freightAmount: deal.freightCost,
            purchaseOrderHireItems: purchaseOrderHireItems,
            purchaseOrderPartItems: purchaseOrderPartItems,
            purchaseOrderProductItems: purchaseOrderProductItems,
            manufactureColumnDefinitionId: deal.manufactureColumnDefinitionId ?? null,
            // Carry the quote's category onto the SO. Without this the field is
            // left unset and serializes as DealCategory.StandardProducts (enum 0),
            // so a custom quote opened as a sales order wrongly showed as
            // "Standard" - and the PO builder also derives its isCustomProduct
            // behaviour from dealCategory, so it affected more than the label.
            dealCategory: this.getSalesOrderCategory(),
            dealId: this.dealId,
        };

        return purchaseOrder;
    }

    // Maps the quote's custom flags to the single DealCategory the sales order
    // carries. Repeat-custom is checked first (more specific than custom). Both
    // custom variants are treated as "custom product" downstream in the PO
    // builder (isCustomProduct = CustomProducts || RepeatCustomProduct).
    private getSalesOrderCategory(): DealCategory {
        if (this.isRepeatCustomProduct()) return DealCategory.RepeatCustomProduct;
        if (this.isCustomQuote()) return DealCategory.CustomProducts;
        return DealCategory.StandardProducts;
    }

    close(prompt: boolean = false) {
        if(prompt) {
            //prompt to save draft or discard


            
            this.dialogRef.close(null);
        }
        else
            this.dialogRef.close(null);
    }
    

    getCategoryName(cat: number) {
        return this.dealHelper.getCategoryName(cat);
    }

    getIncotermName(incoterm: number) {
        switch(incoterm) {
            case Incoterm.EXW: return 'EXW - Ex Works';
            // case Incoterm.FCA: return 'FCA - Free Carrier';
            // case Incoterm.FAS: return 'FAS - Free Alongside Ship';
            // case Incoterm.FOB: return 'FOB - Free On Board';
            // case Incoterm.CFR: return 'CFR - Cost and Freight';
            case Incoterm.CIF: return 'CIF - Cost, Insurance and Freight';
            // case Incoterm.CPT: return 'CPT - Carriage Paid To';
            // case Incoterm.CIP: return 'CIP - Carriage and Insurance Paid To';
            case Incoterm.DAP: return 'DAP - Delivered at Place';
            // case Incoterm.DPU: return 'DPU - Delivered At Place Unloaded';
            // case Incoterm.DDP: return 'DDP - Delivered Duty Paid';
            default: return Incoterm[incoterm];
        }
    }

    getFreightTermName(term: number) {
        switch(term) {
            case FreightTerm.DoorToDoor: return 'Door to Door';
            case FreightTerm.DoorToPort: return 'Door to Port';
            case FreightTerm.CustomerCollect: return 'Customer Collect';
            default: return FreightTerm[term];
        }
    }

    getTransportModeName(mode: number) {
        switch(mode) {
            default: return TransportMode[mode];
        }
    }
    
    getLeadTimeTypeName(type: number) {
        switch(type) {
            default: return LeadTimeType[type];
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // custom products x boms
    public customProductName: string = '';
    public customProductPartNumber: string = '0001-9999';


    // taken from BOM
    public PartUnitType = PartUnitType;

    // Loaded once on init. Used by the create-new-part popup's Storage Location
    // dropdown so new parts inherit a registered bin from the bin/rack
    // management system. Mirrors the bom-builder loader.
    public partLocations: PartLocationDto[] = [];

    // Display the canonical lowercase "mm" for PartUnitType.Mm, otherwise the
    // enum-key string. Used by inline {{...}} bindings in the quote-line grid.
    partUnitTypeName(value: number | null | undefined): string {
        if (value == null) return '';
        if (value === PartUnitType.Mm) return 'mm';
        return PartUnitType[value]?.toString() ?? '';
    }

    // Used by the template *ngIf to switch between an inline unit dropdown
    // and the read-only unit label on the quote-line grid. Mirror of the
    // bom-builder helper. newlyCreatedPartIds is already populated by the
    // existing "Create New" part flow at line ~1090.
    isNewlyCreatedPart(itemControl: FormGroup): boolean {
        var partId = itemControl?.controls['partId']?.value;
        return !!partId && this.newlyCreatedPartIds.has(partId);
    }

    // Inline unit change for a newly-created part. Updates the local part
    // cache and persists via apiPartPartPut. Errors swallowed silently per
    // the same convention as other inline saves in this dialog.
    //
    // The PUT endpoint deserialises into PartResponseWithSubstitutes whose
    // SubstitutePartIds maps to a NOT NULL SQL column. Defensively attach an
    // empty array if it's missing so the DB doesn't reject with NULL.
    changePartUnitType(itemControl: FormGroup, newType: PartUnitType) {
        var partId = itemControl?.controls['partId']?.value;
        var part = this.parts.find(p => p.id === partId);
        if (!part) return;

        part.partUnitType = newType;
        var body: any = { ...part, substitutePartIds: (part as any).substitutePartIds ?? [] };
        this.partService.apiPartPartPut$Json$Response({body}).subscribe();
    }
    
    private readonly componentText = 'Component';
    private readonly assemblyText = 'Assembly';
    private readonly subAssemblyText = 'Sub-Assy';

    isMultiLevel = () => true;

    getLevelIndent(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return 0;
        return [...level].filter(x => x == '.').length;
    }

    getNextLevel() {
        var allLevels = this.partItems?.controls
            .map((x: FormGroup) => x.controls['level']?.value ?? '')
            .filter((x: string) => x.length > 0)
            .map((x: string) => +x.split('.')[0])
            .filter(isNumber)
            ?? [];

        return !allLevels.length ? 1 : (Math.max(...allLevels) + 1);
    }

    hasLevelChildren(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return false;

        var levelChildPrefix = level + '.';

        var allLevels: string[] = this.partItems?.controls.map((x: FormGroup) => x.controls['level']?.value ?? '') ?? [];

        return allLevels.some(x => x.startsWith(levelChildPrefix));
    }

    toggleLevelChildrenHide(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return;

        var levelChildPrefix = level + '.';

        var childControls = this.partItems?.controls
        .filter((itemControl: FormGroup) => itemControl.controls['level']?.value?.startsWith(levelChildPrefix));

        if(!childControls?.length)
            return;

        //get first "hidden" state as nested children could have a mixture of these
        var currentToggleState = (childControls[0] as FormGroup).controls['hidden'].value;

        childControls.forEach((itemControl: FormGroup) => itemControl.controls['hidden'].setValue(!currentToggleState));
    }

    areLevelChildrenHidden(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return false;

        var levelChildPrefix = level + '.';

        var childControls = this.partItems?.controls
        .filter((itemControl: FormGroup) => itemControl.controls['level']?.value?.startsWith(levelChildPrefix));

        if(!childControls?.length)
            return false;

        return !!(childControls[0] as FormGroup).controls['hidden'].value;
    }

    reorderLevels() {
        if(!this.partItems?.controls)
            return;

        this.partItems.controls = this.partItems.controls.sort((a: FormGroup, b: FormGroup) =>
            (a.controls['level'].value > b.controls['level'].value) ? 1 :
            ((b.controls['level'].value > a.controls['level'].value) ? -1 : 0));
    }

    constrainLevelFormat(event, itemControl: FormGroup) {
        var regex = this.isMultiLevel() ? /[^0-9.]/g : /[^0-9]/g;

        event.target.value = event.target.value.replace(regex, '');

        itemControl.controls['level'].setValue(event.target.value);
    }


    getComponentType(itemControl: FormGroup) {
        if(!this.isMultiLevel())
            return this.componentText;

        //if level is a whole number it needs to be treated as a Sub Assembly (within Type) - this then should only populate 'S' part numbers
        var level = itemControl.get('level').value;

        if(!level.includes('.'))
            return this.assemblyText;
        else if(this.hasLevelChildren(itemControl))
            return this.subAssemblyText;
        else
            return this.componentText;
    }
    isAssemblyType = (itemControl: FormGroup) => this.getComponentType(itemControl) != this.componentText;

    // getComponentBgClass(itemControl: FormGroup) {
    //   var type = this.getComponentType(itemControl);

    //   return (type == this.assemblyText || type == this.subAssemblyText) ? 'bg-blue-100' : 'bg-gray-50';
    // }


    getItemSubtotal(itemControl: FormGroup) {
        var cost = itemControl?.get('cost').value ?? 0;
        var quantity = itemControl?.get('quantity').value ?? 1;
        
        return cost * quantity;
    }


    setupFilterParts (itemControl: FormGroup) {
      if(!this.lookupPart(itemControl))
        this.filteredParts = this.parts;
    }
  
    displayPart = (partId: number) => this.formatPartLabel(this.parts?.find(x => x.id == partId));

    // Builds the autocomplete display label for a part. Appends "Rev N" for
    // parts that belong to a version group (multiple sisters), so the search
    // dropdown shows each revision as its own selectable row. Single-revision
    // parts keep the bare number. Mirror of bom-builder.formatPartLabel.
    formatPartLabel(part: any): string {
        if (!part) return '';
        if (!part.partVersionsId || !part.sisterParts || part.sisterParts.length <= 1) return part.number ?? '';
        var rev = part.sisterParts.find(s => s.id === part.id);
        return rev ? `${part.number} Rev ${rev.versionNumber}` : (part.number ?? '');
    }
  
    showPartOption(part: PartResponse, itemControl: FormGroup) {
      return true; // filtering disabled for now
    }
  
    public bom: BomDto;
    public bomAssemblyProcesses: any[] = [];

    
    // Assembly Process - Operation Codes. Server-driven since 2026-08-10, settings-managed
    // on /settings/cost-centres. The hardcoded list this replaces was one of four copies.
    public operationCodes: OperationCodeDto[] = [];

    // Assembly Process - Cost Centers
    public costCentres: CostCentreDto[] = [
        { code: '100TC', name: '100t Calibration Machine', rate: 0.00 },
        { code: '18CAL', name: '18T Calibration Machine', rate: 0.00 },
        { code: '300TC', name: '300T Calibration Machine', rate: 0.00 },
        { code: '3DPRT', name: '3D Printing', rate: 0.00 },
        { code: '60CAL', name: '60t Calibration Machine', rate: 0.00 },
        { code: '94TVU', name: '93t Vulcan Horizontal Test Machine', rate: 0.00 },
        { code: 'ANODI', name: 'Anodising', rate: 0.00 },
        { code: 'ASSEM', name: 'Assembly', rate: 0.00 },
        { code: 'BDMAR', name: 'BD Marine - Calibration', rate: 0.00 },
        { code: 'CURE', name: 'UV Cure', rate: 0.00 },
        { code: 'DURLI', name: 'Durham Lifting - Calibration', rate: 0.00 },
        { code: 'FINAL', name: 'Final Inspection', rate: 0.00 },
        { code: 'GAUGE', name: 'Gauge', rate: 0.00 },
        { code: 'HYJAW', name: 'Hydrajaws Ltd.', rate: 0.00 },
        { code: 'INSPE', name: 'Inspection', rate: 0.00 },
        { code: 'KITTI', name: 'Kitting', rate: 0.00 },
        { code: 'MACHI', name: 'Machining', rate: 0.00 },
        { code: 'NPL', name: 'National Physical Lab - London', rate: 0.00 },
        { code: 'OVEN', name: 'Oven', rate: 0.00 },
        { code: 'PACKI', name: 'Packing', rate: 0.00 },
        { code: 'POSTC', name: 'Post Cure', rate: 0.00 },
        { code: 'PRESS', name: 'Pressure Test Machine', rate: 0.00 },
        { code: 'PROOF', name: 'Proof Load', rate: 0.00 },
        { code: 'R&D', name: 'Research & Development', rate: 0.00 },
        { code: 'RFID', name: 'Fit and program RFID chip', rate: 0.00 },
        { code: 'STORE', name: 'Store 01', rate: 0.00 },
        { code: 'SUBCO', name: 'Sub-Contract', rate: 0.00 },
        { code: 'TRIFO', name: 'Tractel UK Ltd', rate: 0.00 },
        { code: 'WASH', name: 'Wash', rate: 0.00 },
        { code: 'WIRE', name: 'Wire', rate: 0.00 },
        { code: 'PROFI', name: 'Profiling', rate: 0.00 },
        { code: 'WELD', name: 'Welding', rate: 0.00 },
    ];

    addAssemblyProcess(process: any) {
        this.bomAssemblyProcesses.push(process);
        this.cdr.detectChanges();
      }
    
      deleteAssemblyProcess(index: number) {
        this.bomAssemblyProcesses.splice(index, 1);
        this.cdr.detectChanges();
        this.recalculateBomLeadTime();
      }
    
      getOperationName(code: string): string {
        return this.operationCodes.find(x => x.code === code)?.name || code;
      }

      getCostCentreName(code: string): string {
        return this.costCentres.find(x => x.code === code)?.name || code;
      }
    
      getProcessRate(process: any): number {
        if (process.isSubcontracted) {
          return process.subcontractorHourlyRate || 0;
        }
        // The cost centre's live rate (looked up by code) is the source of truth for
        // labour cost. process.costCentreRate is only a snapshot taken when the operation
        // was created/imported, and goes stale - £0 when the rate was set later, or even
        // a different figure than the same code elsewhere. That stale snapshot is why
        // imported operations quoted labour at £0. Fall back to it only when the code
        // matches no current cost centre (e.g. a deleted/renamed one).
        const live = this.costCentres.find(c => c.code === process.costCentreCode)?.rate;
        return live ?? (process.costCentreRate || 0);
      }
    
      getProcessTotal(process: any): number {
        const rate = this.getProcessRate(process);
        const qty = this.getQuantityForProcess(process);
        const setupHours = (process.setupHours || 0) * (process.isSubcontracted ? this.SUBCONTRACT_SETUP_WEEK_TO_HOURS : 1);
        const runHours = (process.runPerUnitHours || 0) * qty;
        return rate * (setupHours + runHours);
      }
    
      // Memoised — see _processesBumpCounter comment block above getProcessesForItem.
      // Returning a fresh array per call here also spams NG0100 because the grouped
      // result derives from this and the template's @for ... track is identity-based.
      private _ccProcessesCache: any[] | null = null;
      private _ccProcessesCacheSrc: any[] | null = null;
      private _ccProcessesCacheLen: number = -1;
      private _ccProcessesCacheBump: number = -1;

      getCostCentreProcesses() {
        const src = this.bomAssemblyProcesses;
        if (
            this._ccProcessesCache &&
            this._ccProcessesCacheSrc === src &&
            this._ccProcessesCacheLen === src.length &&
            this._ccProcessesCacheBump === this._processesBumpCounter
        ) {
            return this._ccProcessesCache;
        }
        this._ccProcessesCache = src.filter(p => !p.isSubcontracted);
        this._ccProcessesCacheSrc = src;
        this._ccProcessesCacheLen = src.length;
        this._ccProcessesCacheBump = this._processesBumpCounter;
        return this._ccProcessesCache;
      }
    
      getCostCentreProcessIndex(process: any): number {
        return this.bomAssemblyProcesses.indexOf(process);
      }
    
      getCostCentreProcessesTotal(): number {
        return this.getCostCentreProcesses().reduce((total, process) => {
          return total + this.getProcessTotal(process);
        }, 0);
      }

      // Groups non-subcontracted assembly processes by cost-centre code so
      // the custom-quote Labour Quote panel renders one summary line per
      // cost centre. Mirror of bom-builder.getGroupedCostCentreProcesses.
      // Memoised — non-cached version rebuilt a fresh Map+array+objects on every CD pass,
      // which combined with the template's `@for ... track group.costCentreCode` was
      // tearing down and recreating views every cycle and spamming NG0100 (see
      // _processesBumpCounter pattern above getProcessesForItem).
      private _ccGroupedCache: { costCentreCode: string, costCentreRate: number, totalHours: number, totalCost: number }[] | null = null;
      private _ccGroupedCacheSrc: any[] | null = null;
      private _ccGroupedCacheLen: number = -1;
      private _ccGroupedCacheBump: number = -1;

      getGroupedCostCentreProcesses(): { costCentreCode: string, costCentreRate: number, totalHours: number, totalCost: number }[] {
        const src = this.bomAssemblyProcesses;
        if (
            this._ccGroupedCache &&
            this._ccGroupedCacheSrc === src &&
            this._ccGroupedCacheLen === src.length &&
            this._ccGroupedCacheBump === this._processesBumpCounter
        ) {
            return this._ccGroupedCache;
        }

        var groups = new Map<string, { costCentreCode: string, costCentreRate: number, totalHours: number, totalCost: number }>();

        for (var process of this.getCostCentreProcesses()) {
          var code = process.costCentreCode || '';
          var qty = this.getQuantityForProcess(process);
          var setupHours = (process.setupHours || 0) * (process.isSubcontracted ? this.SUBCONTRACT_SETUP_WEEK_TO_HOURS : 1);
          var runHours = (process.runPerUnitHours || 0) * qty;
          var hours = setupHours + runHours;
          var rate = this.getProcessRate(process);

          if (!groups.has(code)) {
            groups.set(code, { costCentreCode: code, costCentreRate: rate, totalHours: 0, totalCost: 0 });
          }
          var group = groups.get(code);
          group.totalHours += hours;
          group.totalCost += rate * hours;
        }

        this._ccGroupedCache = Array.from(groups.values());
        this._ccGroupedCacheSrc = src;
        this._ccGroupedCacheLen = src.length;
        this._ccGroupedCacheBump = this._processesBumpCounter;
        return this._ccGroupedCache;
      }
    
      getSubcontractorProcesses() {
        return this.bomAssemblyProcesses.filter(p => p.isSubcontracted && p.subcontractorName);
      }
    
      getSubcontractorProcessIndex(process: any): number {
        return this.bomAssemblyProcesses.indexOf(process);
      }
    
    getSubcontractorProcessTotal(process: any): number {
        const hourlyRate = process.subcontractorHourlyRate || 0;
        const qty = this.getQuantityForProcess(process);
        const setupHours = (process.setupHours || 0) * (process.isSubcontracted ? this.SUBCONTRACT_SETUP_WEEK_TO_HOURS : 1);
        const runHours = (process.runPerUnitHours || 0) * qty;
        return hourlyRate * (setupHours + runHours);
    }
    
    getAssemblySubcontractorTotal(): number {
        return this.getSubcontractorProcesses().reduce((total, process) => {
            return total + this.getSubcontractorProcessTotal(process);
        }, 0);
    }
    
    getMaterialTotal() {
        var total = 0;
        this.partItems?.controls.forEach((itemControl: FormGroup) => total += this.getItemSubtotal(itemControl));
        return total;
    }
    
    getGrandTotal = () => this.getMaterialTotal() + this.getCostCentreProcessesTotal() + this.getAssemblySubcontractorTotal();

    
    // Import-existing dialog — copies steps from a Production manufacture process
    // into the assembly-level process list as new editable rows. Hard copy: production
    // edits never propagate back.
    openImportManufactureProcessDialog(): void {
        const existingMaxSequence = this.bomAssemblyProcesses
            .filter(p => p.bomItemId == null && p.clientLineId == null)
            .reduce((max: number, p: any) => Math.max(max, p.sequence || 0), 0);

        const ref = this.dialog.open(ImportManufactureProcessDialogComponent, {
            data: {
                manufactureProcesses: this.manufactureProcesses,
                existingMaxSequence,
                costCentres: this.costCentres,
            },
            autoFocus: false,
        });

        ref.afterClosed().subscribe((rows: any[] | null) => {
            if (!rows?.length) return;
            this.bomAssemblyProcesses.push(...rows);
            this.recalculateBomLeadTime();
            this.cdr.detectChanges();
        });
    }

    openAssemblyProcessPopup(data: any = {}, isNew: boolean = true) {
        let title = isNew ? 'Add Process Step' : 'Update Process Step';

        // Auto-assign sequence for new processes, scoped to the owning line so per-line
        // and assembly-level sequences stay independent.
        if (isNew) {
            const scopedId = data.bomItemId ?? null;
            const scopedClient = data.clientLineId ?? null;
            const scoped = this.bomAssemblyProcesses.filter(p =>
                (p.bomItemId ?? null) === scopedId && (p.clientLineId ?? null) === scopedClient);
            const maxSequence = scoped.length > 0
                ? Math.max(...scoped.map(p => p.sequence || 0))
                : 0;
            data.sequence = maxSequence + 10;
        }

        // Surface every quote line as a target — including unsaved lines via clientLineId —
        // so users can attach a process to a line they just added in the same session.
        const bomItemOptions = (this.partItems?.controls as FormGroup[] ?? [])
            .map(c => {
                const realId = c.get('bomItemId')?.value as number | null;
                const clientId = c.get('clientLineId')?.value as number | null;
                return {
                    lineKey: (realId && realId > 0) ? realId : clientId,
                    bomItemId: realId,
                    clientLineId: clientId,
                    level: c.get('level')?.value,
                    partNumber: this.parts.find(p => p.id === c.get('partId')?.value)?.number ?? '',
                };
            })
            .filter(o => o.lineKey != null);

        let dialogRef = this.dialog.open(AssemblyProcessPopupComponent, {
        disableClose: true,
            data: {
                title: title,
                payload: data,
                operationCodes: this.operationCodes,
                costCentres: this.costCentres,
                bomItemOptions: bomItemOptions,
            }
        });
    
        dialogRef.afterClosed().subscribe((res) => {
            if (!res) return; // User cancelled

            if (isNew) {
                this.addAssemblyProcess(res);
                this.recalculateBomLeadTime();
            } else {
                // Update existing process
                const index = this.bomAssemblyProcesses.findIndex(p => p === data);
                if (index !== -1)
                    this.bomAssemblyProcesses[index] = res;
                this.recalculateBomLeadTime();
                // In-place replacement: length and array ref are unchanged,
                // so the processes memo wouldn't auto-invalidate on its own.
                this.bumpProcessesCache();
            }
            this.cdr.detectChanges();
        });
    }

    recalculateBomLeadTime() {
        var leadTime = sumList(this.bomAssemblyProcesses?.map(x => (x.setupHours ?? 0) * (x.isSubcontracted ? 35 : 1) + (x.runPerUnitHours ?? 0)) ?? []);
        var leadWeeks = TimeUtil.getWeeksNumber(leadTime);

        this.partItems?.controls?.forEach((itemControl: FormArray) => {
            itemControl.controls['leadTime'].setValue(leadWeeks);
            itemControl.controls['leadTimeType'].setValue(LeadTimeType.Weeks);
        });
    }


    buildBomModel(quote: DealDto, productId: number) {
        this.bom ??= {};

        this.bom.isNotDraft = true;
        this.bom.isPhantom = false;
        this.bom.creatorUserId = this.appAuth.getUser()?.id;
    
        this.bom.isMultiLevel = true;
        this.bom.leadTime = 0;
        this.bom.description = "Custom BOM for " + this.customProductName;
        this.bom.productId = productId;
        this.bom.revision = 0;   // first issue is rev 0 (DLM, 2026-09-03)
    
        this.bom.totalCost = this.getGrandTotal();
        
        // items
        // Preserve bomItemId + clientLineId so the backend can match existing
        // rows on save instead of treating every line as new and inserting a
        // duplicate. Dropping bomItemId here was half of the custom-quote BOM
        // duplication bug (the other half: ids not echoed back to the form
        // after save - see echoBomItemIds).
        this.bom.items = quote.quotePartLines.map(x => <BomItemDto> {
            bomItemId: (x as any).bomItemId ?? null,
            bomId: this.bom.bomId ?? 0,
            level: x.level,
            partId: x.partId,
            quantity: x.quantity,
            sourceType: (x as any).sourceType ?? null,
            clientLineId: (x as any).clientLineId ?? null,
            // Manual cost parts (soft-launch item 1): the line's cost IS its day price, so it goes onto
            // the BOM line instead of being dropped (every other part's BOM cost is Part.Cost).
            manualUnitCost: this.lookupPartById(x.partId)?.usesManualCost ? (x.cost ?? undefined) : undefined,
        });
    
        // assembly processes
        // Mirror British-spelled in-memory fields (costCentreCode/Rate) back
        // to the American spelling the backend DTO expects (costCenterCode/
        // Rate). Without this, cost-centre data is silently dropped on save
        // because the field name doesn't match the JSON contract.
        // Mirror of bom-builder.buildBomModel.
        (this.bom as any).assemblyProcesses = this.bomAssemblyProcesses.map(process => {
            var translated: any = {
                ...process,
                costCenterCode: (process as any).costCentreCode ?? (process as any).costCenterCode,
                costCenterRate: (process as any).costCentreRate ?? (process as any).costCenterRate,
            };
            // Only strip bomId for new BOMs (bomId is 0 or null)
            if (!this.bom.bomId || this.bom.bomId === 0) {
                const { bomId, ...processWithoutBomId } = translated;
                return processWithoutBomId;
            }
            // For existing BOMs, set the correct bomId
            return { ...translated, bomId: this.bom.bomId };
        });
    }


    //pdf
    public downloadPdf() {
        this.preparePdf(PDF => {
            PDF.save(`${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`);

            //move to negotiation
            var form = this.buildModel();
            form.isWizardInProgress = true;
            this.setStageToNegotiation(form);
            this.saveDealApiAsync(form);
        });
    }

    private preparePdf(callback: (pdfDoc: jsPDF) => any) {
        let PDF = new jsPDF("p", "mm", "a4", true);

        this.loader.open();

        //make sure loader fires before starting the pdf gen
        setTimeout(() => {

            //title
            this.addTextToPdf(PDF, [[`QUOTATION`]], 12, null, 50, null, true);
            this.addTextToPdf(PDF, [[`Reference: ${this.deal.quoteNumber || this.deal.quoteRef || ''}`]], 10, null, 56, null, true);

            var cellStyle: Partial<Styles> = { lineColor: "#000000", lineWidth: 0.2, cellPadding: {vertical: 1, horizontal: 2}, fontSize: 8, fontStyle: 'normal', };
            var blackCell: Partial<Styles> = { ...cellStyle, textColor: "#FFFFFF", fillColor: "#000000", cellWidth: 192, };
            var whiteCell: Partial<Styles> = { ...cellStyle, textColor: "#000000", fillColor: "#FFFFFF", cellWidth: 192, };
            var greyCell: Partial<Styles> = { ...cellStyle, textColor: "#000000", fillColor: "#e6e6e6", cellWidth: 38, };

            var blankCell: Partial<Styles> = { ...whiteCell, lineColor: '#FFFFFF', cellWidth: 32, fontSize: 9, cellPadding: {top: 3, bottom: 1, horizontal: 2} };

            var autoTableOptions: UserOptions = {theme: "grid", body: [], tableWidth: 192, margin: {horizontal: 8, top: 52, bottom: 36}, columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 64 }, 2: { cellWidth: 32 }, 3: { cellWidth: 64 }}, };


            //table 1 - specs
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "Customer Details", styles: {...blackCell, cellWidth: 96} },
                        { content: "Quote Details", styles: {...blackCell, cellWidth: 96} },
                    ],
                    [
                        { content: [
                                this.selectedCompanyName ?? '',
                                this.getContactEmail() ?? '',
                                this.getContactPhoneNumber() ?? '',
                                this.getCompany()?.address ?? '',
                            ].join('\n'), styles: {...whiteCell, cellWidth: 96} },
                        { content: [
                                `Title: ${this.customerForm?.controls['name'].value}`,
                                `Contact: ${this.getContactName()}`,
                                `Date: ${this.datePipe.transform(this.getTodaysDate(), 'd MMM yyyy')}`,
                            ].join('\n'), styles: {...whiteCell, cellWidth: 96} },
                    ],
                ],
            });


            //table 2+ - line items
            this.addTextToPdf(PDF, [['Items']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
            
            var lineItems: {isPart: boolean, itemControl: FormGroup}[] = [
                ...this.productItems?.controls.map(x => <any>{isPart: false, itemControl: x}),
                // ...this.partItems?.controls.map(x => <any>{isPart: true, itemControl: x}),
            ];

            lineItems.forEach((x, i) => {
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    ${x.isPart ? 'Part' : 'Product'} ID: ${x.isPart ? this.lookupPart(x.itemControl)?.number : displayProductNumber(this.lookupProduct(x.itemControl)?.partNumber)}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${x.isPart ? this.lookupPart(x.itemControl)?.description : this.lookupProduct(x.itemControl)?.description}
                                
    Specification:
        • Quotation for ${x.isPart ? this.lookupPart(x.itemControl)?.description : this.lookupProduct(x.itemControl)?.description}
        • Product Code: ${x.isPart ? this.lookupPart(x.itemControl)?.number : displayProductNumber(this.lookupProduct(x.itemControl)?.partNumber)}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${x.itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(x.itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            ...(x.isPart ? [] : [{ content: `Discount\n${this.currencyPipe.transform(x.itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell }]),
                            { content: `Lead Time\n${x.itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(x.itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(x.itemControl.controls['cost'].value * x.itemControl.controls['quantity'].value - (x.isPart ? 0 : x.itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            if(this.partItems?.length)
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: 1    Product ID: ${displayProductNumber(this.customProductPartNumber)}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Specification:
        • Quotation for ${this.customProductName}
        • Product Code: ${displayProductNumber(this.customProductPartNumber)}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${1}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${(this.partItems.controls[0] as FormArray).controls['leadTime'].value} ${this.getLeadTimeTypeName((this.partItems.controls[0] as FormArray).controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });

            //calibration, hire, marine
            this.calibrationItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Loadcell ID: ${this.lookupLoadcell(itemControl)?.bookingRef}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${this.lookupLoadcell(itemControl)?.customerCode}
                                
    Specification:
        • Quotation for ${itemControl.controls['description'].value}
        • Product Code: ${this.lookupLoadcell(itemControl)?.serialNumber}
        • Calibrated to industrial standards`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * itemControl.controls['quantity'].value - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            this.hireItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Asset ID: ${this.lookupAsset(itemControl)?.serialNumber}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${this.lookupAsset(itemControl)?.name}
                                
    Specification:
        • Quotation for ${this.lookupAsset(itemControl)?.name}
        • Product Code: ${this.lookupAsset(itemControl)?.serialNumber}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${this.getHireQuantity(itemControl)}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * this.getHireQuantity(itemControl) - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ], //todo
                    ],
                });
            });

            this.marineItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Part: ${itemControl.controls['partName'].value}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${itemControl.controls['supplier'].value}
                                
    Specification:
        • Quotation for ${itemControl.controls['partName'].value}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * itemControl.controls['quantity'].value - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            //payment terms (only if user selected one)
            if (this.summaryForm.controls['paymentTerm'].value) {
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                    head: [
                        [
                            { content: `Payment Terms: ${this.summaryForm.controls['paymentTerm'].value}`, styles: {...greyCell, cellWidth: 192, cellPadding: {vertical: 3, horizontal: 4}} },
                        ],
                    ],
                });
            }

            //table 3 - totals
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "Subtotal:", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier(), 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: "Freight:", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.summaryForm.controls['freightCost'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: `VAT (${this.summaryForm.controls['vatRate'].value * 100}%):`, styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * this.summaryForm.controls['vatRate'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: "Total (inc VAT):", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                ],
            });


            // Notes section: free-typed quote note pulled from the customer form.
            // Only rendered when the user has actually entered a note — most quotes have none.
            const quoteNotes = (this.customerForm?.controls['notes']?.value ?? '').toString().trim();
            if (quoteNotes) {
                this.addTextToPdf(PDF, [[`Notes\n${quoteNotes}`]], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
            }

            //table 4 - disclaimer
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "Please note this quotation is valid for 30 days from the date issued. After this period pricing, availability and lead times may be subject to change.", styles: {...greyCell, cellWidth: 192, cellPadding: {vertical: 3, horizontal: 4}} },
                    ],
                ],
            });
            
            // Add header(s) and footer(s)
            this.addHeaderAndFooter(PDF).then(PDF => {
                this.loader.close();
                callback(PDF);
            });
            
        }, 100);

    }
  
  async addHeaderAndFooter(PDF: jsPDF) {
    // Shared DLM header/footer via addImage (html2canvas-free). Reuses the
    // module-level routine because this dialog extends Paging and TS has no
    // multiple inheritance — see pdf.component.ts drawStandardHeaderAndFooter.
    return drawStandardHeaderAndFooter(PDF);
  }

  setTableOptions(
      PDF: jsPDF,
      startY: number,
      columns: { name: string }[],
      values: RowInput[],
      columnStyles: { [key: string]: Partial<Styles> } = null,
      hasHeader: boolean = false,
      hasFooter: boolean = false,
      // headStyles: { [key: string]: Partial<Styles> } = null
    ): UserOptions {
      return {
        head: [columns.map((x) => x.name)],
        body: values,
        theme: "plain",
        startY: startY,
        margin: {
          top: hasHeader ? 50 : 10,
          bottom: hasFooter ? 30 : 5,
          left: 14,//2.5,
          right: 14,//3.75,
        },
        styles: {
          fontSize: 8,
        },
        columnStyles: columnStyles,
        //   headStyles: headStyles,
        pageBreak: 'avoid',
        willDrawCell: (data) => {
          PDF.setDrawColor("#cccccc");
          PDF.setLineWidth(0.2);
          PDF.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          );
        },
        tableLineColor: "black",
        tableLineWidth: 0, //0.2
        didParseCell: (data) => {
          if (columnStyles && data.cell && data.cell.section === "head") {
            var colIndex = columns.findIndex((x) => x.name == data.cell.raw);
  
            //set header styles to match the column styles here
            if (columnStyles[colIndex]) {
              data.cell.styles.halign = columnStyles[colIndex].halign;
              data.cell.styles.cellWidth = columnStyles[colIndex].cellWidth;
              // data.cell.styles.fontSize = columnStyles[colIndex].fontSize;
              // data.cell.styles.cellPadding = columnStyles[colIndex].cellPadding;
            }
          }
        },
      };
    }

  addTextToPdf(
    PDF: jsPDF,
    data: RowInput[],
    fontSize?: number,
    padding?: number,
    startY?: number,
    offset?: number,
    center?: boolean,
    tableMargin?: MarginPaddingInput,
    hasHeader: boolean = false,
    hasFooter: boolean = false,
  ) {
      var columns = [
          {name: ''},
      ];
      var options = this.setTableOptions(
          PDF,
          (startY ?? ((PDF as any).lastAutoTable.finalY || 15) + (offset ?? 5)),
          columns,
          data,
          null,
          hasHeader,
          hasFooter,
      );
      options.styles = {
          fontSize: fontSize ?? 9,
          cellPadding: padding ?? 0.5,
          halign: center == true ? 'center' : 'left',
      };
      options.willDrawCell = (data) => {
          PDF.setDrawColor("#ffffff");
          PDF.setLineWidth(0);
          PDF.line(0,0,0,0);
      };
      if(tableMargin != null)
        options.margin = tableMargin;
      autoTable(PDF, options);
  }


    // Files
    loadFiles() {
        if(!this.dealId) return;

        this.dealService.apiDealGetFilesGet$Json({ dealId: this.dealId }).subscribe(res => {
            if(!res?.data) return;

            this.fileInfos = res.data;

            this.cdr.detectChanges();
        });
    }

    // async openFileUpload(){
    //     var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

    //     dialog.afterClosed().subscribe((dialogRes) => {
    //         if (!dialogRes) return;

    //         this.uploadImages(dialogRes);
    //     });    
    // }

    openFileUpload(isBom: boolean = false) {
        var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            if(this.dealId) {
                this.uploadImages(dialogRes, isBom);
                return;
            }
            
            for(var file of dialogRes) {
                var extension = '.' + file.name.split('.').pop();
                this.files.push(file);

                this.fileInfos.push({ 
                    dealId: this.dealId, 
                    name: file.name, 
                    type: extension,
                    isBom: isBom,
                });
            }
        });    
    }

    deleteFile(file: QuoteFileDto, event: any){
        event.stopPropagation();

        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title  : "Delete File",
            message: 'Are you sure you want to delete this file?',
            actions: {
                confirm: {
                    label: "Delete",
                },
            },
        });
    
        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if ( result === 'confirmed' )
            {
                if(this.dealId)
                    this.dealService.apiDealDeleteFileFileIdDelete$Json({ fileId: file?.quoteFileId }).subscribe((res) => {
                        if(!res) return;
            
                        this.loadFiles();
                    });
                else {
                    this.fileInfos = this.fileInfos.filter(x => x.name != file.name);
                    this.files = this.files.filter(x => x.name != file.name);
                }
            }
        });
    }

    async uploadImages(files: FileList, isBom: boolean) {
        if (!this.dealId) return;
        
        var newFiles = Array.from(files);

        newFiles
            .forEach((file) => {
                var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
                var blob: Blob = file;
                var name: string = file.name;

                this.dealService.apiDealUploadFileDealIdPost$Json({ 
                    dealId: this.dealId,
                    filename: name,
                    isBom: isBom,
                    body: { formFile: blob },
                }).subscribe((res) => {
                    this.loadFiles();
                });
            });
    }

    download(file: QuoteFileDto){
        this.dealService.apiDealDownloadFileFileIdGet({ fileId: file.quoteFileId }).subscribe((res) => {
            if(!res) return;

            // Convert the response to a Blob
            const blob = new Blob([res], { type: 'application/octet-stream' });

            // Create a link element
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // if the items name doesnt end with the file extension, add it
            if(!file?.name.endsWith(file?.type)){
                file.name += file?.type;
            }

            // Set the download attribute of the link to the desired file name
            link.download = file.name;
            // link.target = '_blank';

            // window.open(url);

            // Simulate a click on the link
            link.click();

            // Release the reference to the blob
            window.URL.revokeObjectURL(url);
        });
    }

    getDaysInSwimlane() {
        if(!this.dealId || !this.deal || !this.deal.dateSinceSwimlaneChange) return null;

        var date = new Date(this.deal.dateSinceSwimlaneChange);
        return !date ? '0 hrs' : TimeUtil.getDaysIntervalText(date, TimeUtil.getNowUTC());
    }

    getSlaDays() {
        if(!this.dealId || !this.deal || !this.deal.dateSinceSwimlaneChange) return null;

        var currentStage = this.stages.find(x => x.stageId = this.deal.stageId);
        if(!currentStage) return null;

        var slaDays = null;
        
        /*
            New Enquiry - 2 working days 
            Follow Up - 1 working day 
            Negotiation - no timer 
            Quote - standard tracking only 
        */
        switch(currentStage.name.toLowerCase().trim()) {
            case 'new enquiry':
                slaDays = 2;
                break;
            case 'follow up':
                slaDays = 1;
                break;
            case 'negotiation':
            case 'quotation':
                break;
        }

        return slaDays;
    }

    calculateSLA() {
        var slaDays = this.getSlaDays();
        if(slaDays == null) return;

        var date = new Date(this.deal.dateSinceSwimlaneChange);

        this.dealService.apiDealAddWorkingDaysGet$Json$Response({date: date.toISOString(), days: slaDays}).subscribe(res => {
            if(!res?.body) return;
            this.slaDate = new Date(res.body);
        });
        
    }
}
